import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Pencil, Trash2, Ban, Printer, Receipt, ChevronDown, ChevronRight, Link2, Unlink, X, Search, HandCoins, FileMinus,
  Paperclip, Download, Mail, FileSpreadsheet, FileText, Clock, Check, CheckCheck
} from 'lucide-react';
import api from '../services/api';
import SelectorMoneda, { useVistaMoneda } from './reportes/SelectorMoneda';
import { useAuth } from '../context/AuthContext';
import Modal from './Modal';
import Paginacion from './Paginacion';
import FacturaForm from './FacturaForm';
import { BotonFotoFactura } from './CargaFacturaIA';
import { formatMonto, formatFecha, formatFechaDeMarca, hoyCaracas } from '../utils/format';
import { pref, aVista, MONEDA_LABEL } from '../utils/moneda';
import { esIndexada, saldoIndexadoBs, pagoIndexadoAFactura, saldoEnVista } from '../utils/indexacion';
import { filaClickeable, FOCO_FILA } from '../utils/filaClickeable';


const ESTADO_BADGE = {
  pendiente: 'bg-amber-50 text-amber-700',
  parcial: 'bg-blue-50 text-blue-700',
  pagada: 'bg-green-50 text-green-700',
  anulada: 'bg-slate-100 text-slate-400 line-through'
};
const ESTADO_LABEL = { pendiente: 'Pendiente', parcial: 'Parcial', pagada: 'Pagada', anulada: 'Anulada' };
const CANAL_LABEL = { tienda: 'Tienda', delivery: 'Delivery' };
const TIPO_DOCUMENTO_LABEL = { factura: 'Factura', nota_debito: 'Nota de débito', nota_credito: 'Nota de crédito' };
const TIPO_NOTA_RUTA = { nota_credito: 'credito', nota_debito: 'debito' };
const TIPO_RETENCION_LABEL = { iva: 'Retención de IVA', islr: 'Retención de ISLR', municipal: 'Retención Municipal' };

// Metáfora de "un check / dos checks" (docs/ESTANDAR-UI.md #10): el
// movimiento del pago está confirmado a mano (un check) hasta que
// Conciliación lo cruza contra el extracto bancario (doble check).
const ESTADO_MOVIMIENTO_PAGO = {
  pendiente: { label: 'Pendiente', clases: 'text-amber-600', Icono: Clock },
  confirmado: { label: 'Por conciliar', clases: 'text-slate-500', Icono: Check },
  conciliado: { label: 'Conciliado', clases: 'text-primary-700', Icono: CheckCheck }
};

// Misma lógica que BadgeVinculoPago, en texto plano (impresión/export).
function etiquetaVinculoPago(movimientoId, estado) {
  if (!movimientoId) return 'Sin conciliar';
  return (ESTADO_MOVIMIENTO_PAGO[estado] || ESTADO_MOVIMIENTO_PAGO.confirmado).label;
}

function BadgeVinculoPago({ movimientoId, estado, className = '' }) {
  if (!movimientoId) {
    return (
      <span className={`inline-flex items-center gap-1 text-amber-500 text-xs whitespace-nowrap ${className}`}>
        <Unlink size={13} /> Sin conciliar
      </span>
    );
  }
  const info = ESTADO_MOVIMIENTO_PAGO[estado] || ESTADO_MOVIMIENTO_PAGO.confirmado;
  const Icono = info.Icono;
  return (
    <span className={`inline-flex items-center gap-1 text-xs whitespace-nowrap ${info.clases} ${className}`}>
      <Icono size={13} /> {info.label}
    </span>
  );
}

const npos = (n) => (Number(n) > 0 ? Number(n) : null);
const round2 = (n) => Math.round(Number(n) * 100) / 100;

// Un pago (forma USD = dólares de cuenta USD, BS = bolívares) -> a la
// moneda de la factura. null si falta la tasa necesaria.
function pagoAMonedaFactura(forma, montoOrig, monedaFactura, bcv, mercado) {
  const x = Number(montoOrig) || 0;
  const b = npos(bcv);
  const m = npos(mercado);
  if (forma === 'USD') {
    if (monedaFactura === 'BS') return m ? x * m : null;
    return x;
  }
  if (monedaFactura === 'BS') return x;
  if (monedaFactura === 'USD') return m ? x / m : null;
  return b ? x / b : null;
}

function pagoAUsdReal(forma, montoOrig, mercado) {
  const x = Number(montoOrig) || 0;
  if (forma === 'USD') return x;
  const m = npos(mercado);
  return m ? x / m : null;
}

// Inverso de pagoAMonedaFactura: cuánto poner (en la forma de la cuenta,
// USD o BS) para cubrir `saldo` de una factura en `monedaFactura`.
function saldoEnForma(saldo, monedaFactura, forma, bcv, mercado) {
  const x = Number(saldo) || 0;
  const b = npos(bcv);
  const m = npos(mercado);
  const r2 = (n) => Math.round(n * 100) / 100;
  if (forma === 'USD') {
    if (monedaFactura === 'BS') return m ? r2(x / m) : null;
    return r2(x);
  }
  if (monedaFactura === 'BS') return r2(x);
  if (monedaFactura === 'USD') return m ? r2(x * m) : null;
  return b ? r2(x * b) : null;
}

// Factura indexada (#151) en la tabla de "Registrar pago": lo necesario
// para recalcular su saldo con la BCV de la fecha del pago.
function lineaIndexada(f) {
  return esIndexada(f)
    ? {
        total: f.total, monto_adeudado: f.monto_adeudado, tasa_bcv: f.tasa_bcv, pagado: Number(f.pagado) || 0,
        bcvHoy: f.indexacion?.bcv_hoy || null
      }
    : null;
}

// Sin BCV cargada para la fecha del pago se muestra con la última conocida
// (el servidor igual exige la del día para guardar).
const saldoVivo = (l, bcv) => (l.idx ? saldoIndexadoBs(l.idx, l.idx.pagado, bcv || l.idx.bcvHoy) : l.saldo);

function esc(v) {
  return String(v == null ? '' : v).replace(/[&<>"]/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]
  ));
}

// Ventana nueva con un documento imprimible autocontenido.
function abrirImpresion(titulo, cuerpoHtml) {
  const w = window.open('', '_blank', 'width=820,height=940');
  if (!w) {
    alert('Habilitá las ventanas emergentes para poder imprimir.');
    return;
  }
  w.document.write(
    '<!doctype html><html><head><meta charset="utf-8"><title>' + esc(titulo) + '</title><style>' +
    '*{box-sizing:border-box}' +
    'body{font-family:system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;color:#1e293b;margin:32px;font-size:13px}' +
    'h1{font-size:18px;margin:0 0 4px}' +
    '.muted{color:#64748b;margin:2px 0}' +
    '.hdr{display:flex;justify-content:space-between;align-items:flex-start;gap:24px;border-bottom:2px solid #0f172a;padding-bottom:14px;margin-bottom:16px}' +
    '.brand{font-size:24px;font-weight:800;letter-spacing:-.02em;line-height:1.15;color:#0f172a}' +
    '.doc{text-align:right;white-space:nowrap;flex-shrink:0}' +
    '.doc .n{font-size:17px;font-weight:700;color:#0f172a}' +
    '.doc .d{color:#475569;margin-top:3px}' +
    '.doc .meta{color:#64748b;font-size:11px;margin-top:6px;text-transform:uppercase;letter-spacing:.04em}' +
    '.cli{margin:0 0 6px;font-size:14px}' +
    'table{width:100%;border-collapse:collapse;margin-top:14px}' +
    'th,td{text-align:left;padding:6px 8px;border-bottom:1px solid #e2e8f0}' +
    'thead th{font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:#64748b;border-bottom:1.5px solid #cbd5e1}' +
    'th.num,td.num{text-align:right}' +
    'tfoot td{border-bottom:none;font-weight:700;border-top:1.5px solid #cbd5e1;font-size:14px}' +
    '.box{margin-top:16px;padding:10px 12px;background:#f8fafc;border-radius:8px}' +
    '.nota{margin-top:16px;padding:10px 12px;border-left:3px solid #cbd5e1;background:#f8fafc;color:#475569}' +
    '.saldo{font-size:15px}' +
    '@media print{body{margin:12mm}}' +
    '</style></head><body>' + cuerpoHtml +
    '<script>window.onload=function(){window.print()}</script></body></html>'
  );
  w.document.close();
}

function imprimirFactura({ empresaNombre, parteLabel, parteNombre, saldoLabel, factura, detalle }) {
  const pm = pref(detalle.moneda);
  const lineas = (detalle.lineas || []).map((l) =>
    '<tr><td>' + esc(l.descripcion) + (l.exento ? ' (exento)' : '') + '</td><td>' + esc(l.categoria_nombre || l.producto_nombre || '') + '</td>' +
    '<td class="num">' + formatMonto(l.cantidad) + '</td>' +
    '<td class="num">' + pm + formatMonto(l.precio_unitario) + '</td>' +
    '<td class="num">' + pm + formatMonto(l.subtotal) + '</td></tr>'
  ).join('');
  const pagos = (detalle.pagos || []).map((p) =>
    '<tr><td>' + formatFecha(p.fecha) + '</td><td>' + esc(p.cuenta_nombre || '') + '</td>' +
    '<td>' + esc(p.referencia || '') + '</td>' +
    '<td>' + etiquetaVinculoPago(p.movimiento_id, p.movimiento_estado) + '</td>' +
    '<td class="num">' + pref(p.forma) + formatMonto(p.monto_original) + '</td>' +
    '<td class="num">' + pm + formatMonto(p.monto_factura) + '</td></tr>'
  ).join('');
  abrirImpresion('Factura ' + factura.numero,
    '<div class="hdr">' +
      '<div class="brand">' + esc(empresaNombre || '') + '</div>' +
      '<div class="doc">' +
        '<div class="n">Factura #' + esc(factura.numero) + '</div>' +
        '<div class="d">' + formatFecha(factura.fecha) + '</div>' +
        '<div class="meta">' + (MONEDA_LABEL[detalle.moneda] || detalle.moneda) +
          (factura.canal ? ' &middot; ' + (CANAL_LABEL[factura.canal] || factura.canal) : '') + '</div>' +
      '</div>' +
    '</div>' +
    '<p class="cli"><strong>' + esc(parteLabel || 'Cliente') + ':</strong> ' + esc(parteNombre || '') + '</p>' +
    '<table><thead><tr><th>Descripcion</th><th>Categoria</th><th class="num">Cant.</th>' +
      '<th class="num">Precio</th><th class="num">Subtotal</th></tr></thead><tbody>' + lineas + '</tbody>' +
      '<tfoot><tr><td colspan="4" class="num">Total (base)</td><td class="num">' + pm + formatMonto(detalle.total) + '</td></tr>' +
      (Number(detalle.monto_iva) > 0
        ? '<tr><td colspan="4" class="num">IVA</td><td class="num">' + pm + formatMonto(detalle.monto_iva) + '</td></tr>' +
          '<tr><td colspan="4" class="num">Total c/IVA</td><td class="num">' + pm + formatMonto(Number(detalle.total) + Number(detalle.monto_iva)) + '</td></tr>'
        : '') +
      (detalle.retenciones || []).map((r) =>
        '<tr><td colspan="4" class="num">' + esc(TIPO_RETENCION_LABEL[r.tipo] || r.tipo) + '</td><td class="num">&minus; ' + pm + formatMonto(r.monto) + '</td></tr>'
      ).join('') +
      (!(detalle.retenciones || []).length && Number(detalle.retencion_iva_monto) > 0
        ? '<tr><td colspan="4" class="num">Retención IVA del cliente</td><td class="num">&minus; ' + pm + formatMonto(detalle.retencion_iva_monto) + '</td></tr>'
        : '') +
      ((detalle.retenciones || []).length > 0 || Number(detalle.retencion_iva_monto) > 0
        ? '<tr><td colspan="4" class="num">Total neto ' + ((detalle.retenciones || []).length > 0 ? 'a pagar' : 'a cobrar') + '</td><td class="num">' + pm + formatMonto(detalle.monto_adeudado) + '</td></tr>'
        : '') +
      '</tfoot></table>' +
    (pagos
      ? '<div class="box"><strong>Pagos</strong><table><thead><tr><th>Fecha</th><th>Cuenta</th><th>Referencia</th>' +
        '<th>Conciliado</th><th class="num">Recibido</th><th class="num">Aplicado (' + (MONEDA_LABEL[detalle.moneda] || detalle.moneda) + ')</th></tr></thead><tbody>' + pagos + '</tbody></table></div>'
      : '') +
    (detalle.notas ? '<p class="nota">' + esc(detalle.notas) + '</p>' : '') +
    '<p class="box saldo">' + esc(saldoLabel || 'Saldo pendiente') + ': <strong>' + pm + formatMonto(detalle.saldo) + '</strong></p>'
  );
}

function imprimirPago({ empresaNombre, parteLabel, parteNombre, factura, pago }) {
  const pm = pref(pago.forma);
  const monedaFactura = factura.moneda;
  const conv = pago.forma === 'BS' && monedaFactura === 'BCV'
    ? 'Tasa BCV: ' + formatMonto(pago.tasa_bcv, 2)
    : pago.forma === 'BS' && monedaFactura !== 'BS'
      ? 'Tasa mercado: ' + formatMonto(pago.tasa_mercado, 2)
      : '';
  abrirImpresion('Pago factura ' + factura.numero,
    '<h1>Comprobante de pago</h1>' +
    '<p class="muted">' + esc(empresaNombre || '') + ' &middot; Factura #' + esc(factura.numero) +
      ' (' + (MONEDA_LABEL[monedaFactura] || monedaFactura) + ')</p>' +
    '<p class="muted">' + esc(parteLabel || 'Cliente') + ': ' + esc(parteNombre || '') + '</p>' +
    '<table><tbody>' +
    '<tr><td>Fecha</td><td class="num">' + formatFecha(pago.fecha) + '</td></tr>' +
    '<tr><td>Cuenta</td><td class="num">' + esc(pago.cuenta_nombre || '-') + '</td></tr>' +
    '<tr><td>Monto recibido</td><td class="num">' + pm + formatMonto(pago.monto_original) + '</td></tr>' +
    (conv ? '<tr><td>Conversión</td><td class="num">' + conv + '</td></tr>' : '') +
    '<tr><td>Aplicado a la factura</td><td class="num">' + pref(monedaFactura) + formatMonto(pago.monto_factura) + '</td></tr>' +
    '<tr><td>Referencia</td><td class="num">' + esc(pago.referencia || '-') + '</td></tr>' +
    '<tr><td>Movimiento</td><td class="num">' +
      (pago.movimiento_id
        ? formatFecha(pago.movimiento_fecha) + ' &middot; ' + esc(pago.movimiento_cuenta || '')
        : 'Sin conciliar') +
      '</td></tr>' +
    '</tbody></table>'
  );
}

// Recibo consolidado de un pago (posiblemente repartido entre varias
// facturas): "Total del pago" es el monto real que se movió en esa
// transacción (USD real), el mismo que se muestra en la pestaña "Pagos".
function imprimirPagoGrupo({ empresaNombre, parteLabel, parteNombre, pago }) {
  const filas = (pago.aplicaciones || []).map((a) =>
    '<tr><td>Factura #' + esc(a.numero) + '</td><td class="num">' +
    pref(a.factura_moneda) + formatMonto(a.monto_factura) + '</td></tr>'
  ).join('');
  const extra =
    (pago.anticipo_generado > 0.02
      ? '<tr><td>Anticipo generado (sobrante)</td><td class="num">$ ' + formatMonto(pago.anticipo_generado) + '</td></tr>'
      : '') +
    (pago.anticipo_consumido > 0.02
      ? '<tr><td>Anticipo aplicado (saldo a favor)</td><td class="num">$ ' + formatMonto(pago.anticipo_consumido) + '</td></tr>'
      : '');
  abrirImpresion('Recibo de pago',
    '<h1>Comprobante de pago</h1>' +
    '<p class="muted">' + esc(empresaNombre || '') + '</p>' +
    '<p class="muted">' + esc(parteLabel || 'Cliente') + ': ' + esc(parteNombre || '') + '</p>' +
    '<table><tbody>' +
    '<tr><td>Fecha</td><td class="num">' + formatFecha(pago.fecha) + '</td></tr>' +
    '<tr><td>Cuenta</td><td class="num">' + esc(pago.cuenta_nombre || '-') + '</td></tr>' +
    '<tr><td>Referencia</td><td class="num">' + esc(pago.referencia || '-') + '</td></tr>' +
    '<tr><td>Movimiento</td><td class="num">' + etiquetaVinculoPago(pago.movimiento_id, pago.movimiento_estado) + '</td></tr>' +
    '</tbody></table>' +
    '<div class="box"><strong>Facturas cubiertas</strong><table><tbody>' + (filas || '<tr><td colspan="2">Ninguna — pago aplicado como anticipo</td></tr>') + extra + '</tbody>' +
    '<tfoot><tr><td>Total del pago</td><td class="num">$ ' + formatMonto(pago.monto_usd) + '</td></tr></tfoot></table></div>'
  );
}

// Comprobante de retención (#35/#97) — uno por tipo (iva/islr/municipal)
// de la factura de compra, siguiendo el formato de las plantillas SENIAT
// que definieron el cálculo (empresa/RIF/Providencia, línea de la factura,
// monto retenido).
function imprimirComprobanteRetencion({ empresa, parteNombre, parteRif, factura, retencion }) {
  const label = TIPO_RETENCION_LABEL[retencion.tipo] || 'Retención';
  abrirImpresion(label + ' ' + retencion.numero_comprobante,
    '<div class="hdr">' +
      '<div class="brand">' + esc(empresa?.empresa_nombre || '') + '</div>' +
      '<div class="doc">' +
        '<div class="n">' + esc(label) + '</div>' +
        '<div class="d">Comprobante N&deg; ' + esc(retencion.numero_comprobante) + '</div>' +
        '<div class="meta">Periodo ' + esc(String(retencion.fecha).slice(0, 7)) + '</div>' +
      '</div>' +
    '</div>' +
    (retencion.tipo === 'iva' && empresa?.numero_providencia_retencion
      ? '<p class="muted">Agente de retención según Providencia ' + esc(empresa.numero_providencia_retencion) +
        (empresa.fecha_providencia_retencion ? ' del ' + formatFecha(empresa.fecha_providencia_retencion) : '') + '</p>'
      : '') +
    (empresa?.rfc_nit ? '<p class="muted">RIF Agente de Retención: ' + esc(empresa.rfc_nit) + '</p>' : '') +
    (empresa?.direccion_fiscal ? '<p class="muted">' + esc(empresa.direccion_fiscal) + '</p>' : '') +
    '<p class="cli"><strong>Proveedor / Sujeto retenido:</strong> ' + esc(parteNombre || '') + (parteRif ? ' &mdash; RIF ' + esc(parteRif) : '') + '</p>' +
    '<table><tbody>' +
      '<tr><td>Factura</td><td class="num">#' + esc(factura.numero) + ' &middot; ' + formatFecha(factura.fecha) + '</td></tr>' +
      '<tr><td>Base imponible</td><td class="num">' + formatMonto(retencion.base_imponible) + '</td></tr>' +
      (retencion.porcentaje != null ? '<tr><td>% aplicado</td><td class="num">' + retencion.porcentaje + '%</td></tr>' : '') +
      (retencion.tipo === 'islr'
        ? '<tr><td>Concepto</td><td class="num">' + esc(retencion.islr_concepto || '') + '</td></tr>' +
          '<tr><td>Sustraendo</td><td class="num">' + formatMonto(retencion.sustraendo || 0) + '</td></tr>'
        : '') +
    '</tbody>' +
    '<tfoot><tr><td>Monto retenido</td><td class="num">' + formatMonto(retencion.monto) + '</td></tr></tfoot></table>'
  );
}

// ============================================================
// Estado de cuenta: facturas + pagos multimoneda — #77 (venta) / #41 (compra)
//
// `parte` = 'cliente' (facturas de venta, /facturas) | 'proveedor'
// (facturas de compra, /compras). Todo lo demás es idéntico: la única
// diferencia real es de vocabulario, el endpoint, el tipo de categoría
// (ingreso/egreso), el tipo de movimiento a conciliar y que la compra no
// autoenumera (el número lo pone el proveedor) ni usa canal.
// ============================================================
const CONFIG_PARTE = {
  cliente: {
    api: '/facturas', param: 'cliente_id', catTipo: 'ingreso', movLabel: 'ingreso',
    label: 'Cliente', saldoCol: 'pendiente_cobrar_usd', autoNumero: true, usaCanal: true,
    doc: 'factura', docPlural: 'facturas', nueva: 'Nueva factura', sinFacturas: 'Este cliente todavía no tiene facturas.',
    catPlaceholder: 'Categoría de venta…', numeroLabel: 'Factura #', colNumero: 'Factura #',
    pagoCuentaLabel: 'Cuenta bancaria donde se recibe', saldoLabel: 'Saldo pendiente'
  },
  proveedor: {
    api: '/compras', param: 'proveedor_id', catTipo: 'egreso', movLabel: 'egreso',
    label: 'Proveedor', saldoCol: 'pendiente_pagar_usd', autoNumero: false, usaCanal: false,
    doc: 'factura de compra', docPlural: 'facturas de compra', nueva: 'Cargar factura de compra',
    sinFacturas: 'Este proveedor todavía no tiene facturas de compra cargadas.',
    catPlaceholder: 'Categoría del gasto…', numeroLabel: 'N° del proveedor', colNumero: 'N° factura',
    pagoCuentaLabel: 'Cuenta bancaria de donde sale', saldoLabel: 'Saldo por pagar'
  }
};

// Abre en otra pestaña un archivo del backend (la foto o el PDF original de
// una factura): la pestaña se abre al instante (si no, el navegador la
// bloquea por llegar después del clic) y se llena cuando baja el archivo.
async function verArchivo(url) {
  const ventana = window.open('', '_blank');
  try {
    const resp = await api.get(url, { responseType: 'blob' });
    const href = window.URL.createObjectURL(new Blob([resp.data], { type: resp.headers?.['content-type'] || resp.data.type }));
    if (ventana) ventana.location.href = href;
    else window.location.href = href;
  } catch {
    if (ventana) ventana.close();
    alert('No se pudo abrir la factura original');
  }
}

// Descarga un archivo del backend (blob) con el nombre que manda el
// servidor en Content-Disposition, o `nombreDefault`.
async function descargarArchivo(url, nombreDefault) {
  try {
    const resp = await api.get(url, { responseType: 'blob' });
    const cd = resp.headers?.['content-disposition'] || '';
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    const nombre = m ? decodeURIComponent(m[1]) : nombreDefault;
    const href = window.URL.createObjectURL(new Blob([resp.data]));
    const link = document.createElement('a');
    link.href = href;
    link.download = nombre;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(href);
  } catch {
    alert('No se pudo descargar el archivo');
  }
}

// `extraccionPendiente` (#133, solo compra): propuesta de la IA que la
// pantalla de Proveedores ya ruteó a este proveedor; abre el formulario
// precargado y avisa con `onExtraccionUsada`. `onFacturaIA` recibe lo que
// lee el botón de cámara de este estado de cuenta, para que Proveedores
// decida a qué proveedor va.
// `accionInicial` ('pago' | 'factura'): viene de una acción rápida del Inicio;
// abre ese formulario apenas carga y avisa con `onAccionInicialUsada`.
export default function EstadoCuenta({
  parte = 'cliente', entidad, empresaNombre, esRestaurante, onSaldoCambiado,
  extraccionPendiente = null, onExtraccionUsada, onFacturaIA,
  accionInicial = null, onAccionInicialUsada
}) {
  const navigate = useNavigate();
  const CP = CONFIG_PARTE[parte] || CONFIG_PARTE.cliente;
  const usaCanal = esRestaurante && CP.usaCanal;
  const usaRecurrente = parte === 'proveedor';
  const { empresaActivaData } = useAuth();
  const [facturas, setFacturas] = useState([]);
  const [pagina, setPagina] = useState(1);
  // Filtros del estado de cuenta (#94): rango por fecha de la factura y
  // estado. Los mismos viajan al PDF/Excel y al correo.
  const FILTROS_VACIOS = { desde: '', hasta: '', estado: '' };
  const [filtros, setFiltros] = useState(FILTROS_VACIOS);
  const hayFiltros = !!(filtros.desde || filtros.hasta || filtros.estado);
  function cambiarFiltro(campo, valor) {
    setFiltros((f) => ({ ...f, [campo]: valor }));
    setPagina(1);
    setExpandidaId(null);
  }
  const [envioEC, setEnvioEC] = useState(null); // { email, enviando, error }
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [total, setTotal] = useState(0);
  const [resumen, setResumen] = useState(null); // { saldo_usd, saldo_bcv }
  const [cargando, setCargando] = useState(false);
  const [expandidaId, setExpandidaId] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [verEn, setVerEn] = useVistaMoneda(); // USD real | BCV (selector común, recordado)

  const [cuentas, setCuentas] = useState([]);

  // Solo compra: {facturaId} abre <FacturaForm/> en un modal. La venta
  // navega a su pantalla propia.
  const [formFactura, setFormFactura] = useState(null);
  // Nota de crédito/débito a punto de crearse: guarda el tipo mientras el
  // usuario elige sobre qué factura se hace.
  const [notaTipo, setNotaTipo] = useState(null);
  const [anticipos, setAnticipos] = useState([]);
  const [formPago, setFormPago] = useState(null);
  const [errorPago, setErrorPago] = useState('');
  const [formPagoRep, setFormPagoRep] = useState(null); // pago repartido (varias facturas + anticipo)
  const [errorPagoRep, setErrorPagoRep] = useState('');
  const [popupVinculo, setPopupVinculo] = useState(false);

  // ---------- Pestaña "Pagos" (pago como unidad, agrupado por pago_grupo) ----------
  const [vista, setVista] = useState('cuenta'); // 'cuenta' | 'pagos'
  const [pagos, setPagos] = useState([]);
  const [pagosPagina, setPagosPagina] = useState(1);
  const [pagosTotalPaginas, setPagosTotalPaginas] = useState(1);
  const [pagosTotal, setPagosTotal] = useState(0);
  const [pagosCargando, setPagosCargando] = useState(false);
  const [pagosOrden, setPagosOrden] = useState({ campo: 'fecha', dir: 'desc' });
  const [pagoExpandidoId, setPagoExpandidoId] = useState(null);

  // Alta rápida de producto / categoría desde una línea de la factura, sin
  // cerrar el formulario. { i: índice de línea, nombre: prefijo escrito }

  const parteId = entidad?.id;

  const cargarFacturas = useCallback(() => {
    if (!parteId) return;
    setCargando(true);
    const params = { [CP.param]: parteId, pagina };
    Object.entries(filtros).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get(CP.api, { params })
      .then(({ data }) => {
        setFacturas(data.datos);
        setTotalPaginas(data.totalPaginas);
        setTotal(data.total);
        setResumen(data.resumen || null);
      })
      .finally(() => setCargando(false));
  }, [parteId, pagina, filtros, CP.api, CP.param]);

  const cargarAnticipos = useCallback(() => {
    if (!parteId) { setAnticipos([]); return; }
    api.get(`${CP.api}/anticipos`, { params: { [CP.param]: parteId } })
      .then(({ data }) => setAnticipos(data.movimientos || []))
      .catch(() => setAnticipos([]));
  }, [parteId, CP.api, CP.param]);

  const cargarPagos = useCallback(() => {
    if (!parteId) return;
    setPagosCargando(true);
    api.get(`${CP.api}/pagos`, {
      params: { [CP.param]: parteId, pagina: pagosPagina, orden: pagosOrden.campo, dir: pagosOrden.dir }
    })
      .then(({ data }) => {
        setPagos(data.datos);
        setPagosTotalPaginas(data.totalPaginas);
        setPagosTotal(data.total);
      })
      .finally(() => setPagosCargando(false));
  }, [parteId, pagosPagina, pagosOrden, CP.api, CP.param]);

  function ordenarPagosPor(campo) {
    setPagosPagina(1);
    setPagosOrden((o) => (o.campo === campo ? { campo, dir: o.dir === 'asc' ? 'desc' : 'asc' } : { campo, dir: 'asc' }));
  }

  useEffect(() => { setPagina(1); setExpandidaId(null); setVista('cuenta'); setPagosPagina(1); setPagoExpandidoId(null); setFiltros(FILTROS_VACIOS); }, [parteId]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { cargarFacturas(); }, [cargarFacturas]);
  useEffect(() => { cargarAnticipos(); }, [cargarAnticipos]);
  useEffect(() => { cargarPagos(); }, [cargarPagos]);
  const [cuentasListas, setCuentasListas] = useState(false);
  useEffect(() => {
    api.get('/cuentas')
      .then(({ data }) => setCuentas(data.filter((c) => c.activo)))
      .catch(() => {})
      .finally(() => setCuentasListas(true));
  }, [parteId]);

  // El pago necesita las cuentas cargadas (elige la primera por defecto).
  useEffect(() => {
    if (!accionInicial || !parteId || !cuentasListas) return;
    if (accionInicial === 'pago') abrirPagoRepartido();
    else if (accionInicial === 'factura') abrirNuevaFactura();
    onAccionInicialUsada?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accionInicial, parteId, cuentasListas]);

  function toggleExpandir(f) {
    if (expandidaId === f.id) { setExpandidaId(null); setDetalle(null); return; }
    setExpandidaId(f.id);
    setDetalle(null);
    setCargandoDetalle(true);
    api.get(`${CP.api}/${f.id}`)
      .then(({ data }) => setDetalle(data))
      .finally(() => setCargandoDetalle(false));
  }

  useEffect(() => {
    if (!extraccionPendiente || parte !== 'proveedor') return;
    setFormFactura({ facturaId: null, extraccion: extraccionPendiente });
    onExtraccionUsada?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraccionPendiente]);

  // Correo con el comprobante de retención en PDF (#133): el usuario
  // confirma o corrige el destinatario antes de mandarlo.
  const [envioRetencion, setEnvioRetencion] = useState(null); // { retencion, email, enviando, error }
  async function enviarRetencion(e) {
    e.preventDefault();
    setEnvioRetencion((v) => ({ ...v, enviando: true, error: '' }));
    try {
      await api.post(`/compras/retenciones/${envioRetencion.retencion.id}/enviar`, { email: envioRetencion.email });
      alert(`Comprobante enviado a ${envioRetencion.email}`);
      setEnvioRetencion(null);
      onSaldoCambiado?.(); // recarga el proveedor por si se le guardó el correo
    } catch (err) {
      setEnvioRetencion((v) => ({ ...v, enviando: false, error: err.response?.data?.error || 'No se pudo enviar el correo' }));
    }
  }

  // Estado de cuenta como documento (#94): PDF/Excel con los filtros de la
  // pantalla y la vista USD/BCV, o el PDF por correo.
  function paramsEstadoCuenta() {
    const q = { [CP.param]: parteId, ver: verEn };
    Object.entries(filtros).forEach(([k, v]) => { if (v) q[k] = v; });
    return q;
  }
  function descargarEstadoCuenta(formato) {
    const qs = new URLSearchParams(paramsEstadoCuenta()).toString();
    return descargarArchivo(`${CP.api}/estado-cuenta/${formato}?${qs}`, `estado-de-cuenta.${formato === 'excel' ? 'xlsx' : 'pdf'}`);
  }
  async function enviarEstadoCuenta(e) {
    e.preventDefault();
    setEnvioEC((v) => ({ ...v, enviando: true, error: '' }));
    try {
      await api.post(`${CP.api}/estado-cuenta/enviar`, { ...paramsEstadoCuenta(), email: envioEC.email });
      alert(`Estado de cuenta enviado a ${envioEC.email}`);
      setEnvioEC(null);
      if (!entidad?.email) onSaldoCambiado?.(); // recarga la ficha: se le guardó el correo
    } catch (err) {
      setEnvioEC((v) => ({ ...v, enviando: false, error: err.response?.data?.error || 'No se pudo enviar el correo' }));
    }
  }

  function refrescar() {
    cargarFacturas();
    cargarAnticipos();
    cargarPagos();
    if (expandidaId) {
      api.get(`${CP.api}/${expandidaId}`).then(({ data }) => setDetalle(data)).catch(() => {});
    }
    onSaldoCambiado?.();
  }

  async function eliminarAnticipo(a) {
    const msg = Number(a.aplicaciones) > 0
      ? 'Este anticipo forma parte de un pago repartido. Se van a revertir TAMBIÉN sus aplicaciones a facturas. ¿Seguir?'
      : '¿Eliminar este anticipo?';
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`${CP.api}/anticipo/${a.id}`);
      refrescar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar el anticipo');
    }
  }

  // Tasas del día (solo lectura): la BCV es oficial y nunca se escribe;
  // la de mercado se puede sobreescribir con el campo opcional "Tasa USD".
  async function traerTasasDia(fecha) {
    try {
      const { data } = await api.get(`${CP.api}/tasas`, { params: { fecha } });
      return { bcv: data.bcv, mercado: data.mercado };
    } catch {
      return { bcv: null, mercado: null };
    }
  }

  // ---------- Formulario de factura ----------
  // La VENTA se carga en pantalla propia (/clientes/:id/factura/...): tiene
  // demasiados campos para un modal y así queda enlazable. La COMPRA sigue
  // en el modal de esta misma pantalla. Las dos usan <FacturaForm/>.
  function abrirNuevaFactura() {
    if (parte === 'cliente') {
      navigate(`/clientes/${parteId}/factura/nueva`, { state: { entidad } });
      return;
    }
    setFormFactura({ facturaId: null });
  }

  function abrirEditarFactura(f) {
    if (parte === 'cliente') {
      navigate(`/clientes/${parteId}/factura/${f.id}`, { state: { entidad } });
      return;
    }
    setFormFactura({ facturaId: f.id });
  }

  // Nota de crédito/débito: siempre nace de una factura anterior, así que el
  // acceso arranca eligiendo cuál afecta.
  function abrirNota(tipo, factura) {
    setNotaTipo(null);
    navigate(`/clientes/${parteId}/nota/${tipo}/${factura.id}`, { state: { entidad } });
  }

  async function anularFactura(f) {
    if (!window.confirm(`¿Anular la ${CP.doc} "${f.numero}"? Sus pagos quedan registrados pero deja de contar en el saldo.`)) return;
    try {
      await api.post(`${CP.api}/${f.id}/anular`);
      refrescar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo anular');
    }
  }

  // ---------- Formulario de pago ----------
  // La moneda del monto la define la cuenta (Bs -> bolívares, resto ->
  // dólares). El sistema convierte a la moneda de la factura para el saldo.
  // Misma tasa para BCV y para mercado: la del día del pago, salvo que se
  // escriba una manual -- nunca la que quedó fija en la factura, para no
  // tener dos reglas distintas según la moneda de la factura.
  const cuentaDePago = cuentas.find((c) => String(c.id) === String(formPago?.cuenta_id));
  const pagoEnBs = cuentaDePago?.moneda === 'BS';
  const pagoForma = pagoEnBs ? 'BS' : 'USD';
  const monedaFacturaPago = formPago?.facturaMoneda || 'USD';
  const mercadoPago = formPago
    ? (Number(formPago.tasa_usd) > 0 ? Number(formPago.tasa_usd) : Number(formPago.tasasDia?.mercado))
    : null;
  const bcvPago = Number(formPago?.tasasDia?.bcv) || null;

  // Lo que se aplica al saldo de la factura (en la moneda de la factura).
  const pagoAplicado = useMemo(() => {
    if (!formPago) return null;
    const base = pagoAMonedaFactura(pagoForma, formPago.monto_original, monedaFacturaPago, bcvPago, mercadoPago);
    if (base == null || !formPago.facturaIdx) return base;
    return pagoIndexadoAFactura(formPago.facturaIdx, formPago.facturaIdx.pagadoPrevio, base, bcvPago);
  }, [formPago, pagoForma, monedaFacturaPago, bcvPago, mercadoPago]);

  const pagoUsdReal = useMemo(() => {
    if (!formPago) return null;
    return pagoAUsdReal(pagoForma, formPago.monto_original, mercadoPago);
  }, [formPago, pagoForma, mercadoPago]);

  // Registrar pago repartido: un solo pago que se distribuye entre varias
  // facturas abiertas de la parte; el sobrante queda como anticipo.
  async function abrirPagoRepartido() {
    setErrorPagoRep('');
    const cuentaDefault = cuentas[0]?.id ? String(cuentas[0].id) : '';
    const [tasasDia, abiertas] = await Promise.all([
      traerTasasDia(hoyCaracas()),
      api.get(`${CP.api}/abiertas`, { params: { [CP.param]: parteId } }).then((r) => r.data).catch(() => ({ facturas: [], anticipo_usd: 0 }))
    ]);
    setFormPagoRep({
      fecha: hoyCaracas(),
      cuenta_id: cuentaDefault,
      monto_original: '',
      tasa_usd: '',
      referencia: '',
      anticipo_aplicado: '',
      anticipoDisp: Number(abiertas.anticipo_usd) || 0,
      movimiento_id: null,
      movimientoInfo: null,
      tasasDia,
      autoReparto: true,
      lineas: (abiertas.facturas || []).map((f) => ({
        factura_id: f.id, numero: f.numero, fecha: f.fecha, moneda: f.moneda,
        saldo: Number(f.saldo) || 0,
        idx: lineaIndexada(f),
        aplicar: ''
      }))
    });
  }

  // Mientras nadie escriba a mano en "Aplicar", cambiar monto, cuenta, fecha
  // o tasa vuelve a repartir solo: antes había que pulsar "Repartir
  // automático" y, si se olvidaba, todo el pago quedaba como anticipo en USD
  // y la factura en BCV seguía abierta (saldos chicos que no son deuda real).
  const CAMPOS_REPARTO = ['monto_original', 'cuenta_id', 'tasa_usd', 'anticipo_aplicado', 'fecha', 'tasasDia'];
  function guardarPagoRepValor(patch) {
    setFormPagoRep((fp) => {
      const nuevo = { ...fp, ...patch };
      return nuevo.autoReparto && Object.keys(patch).some((k) => CAMPOS_REPARTO.includes(k)) ? repartir(nuevo) : nuevo;
    });
  }

  function repartirAuto() {
    setFormPagoRep((fp) => repartir(fp));
  }

  // Auto-reparto: llena "aplicar" de cada factura (más vieja primero) con
  // el saldo convertido a la forma de la cuenta, hasta agotar monto + anticipo.
  function repartir(fp) {
    const cta = cuentas.find((c) => String(c.id) === String(fp.cuenta_id));
    const forma = cta?.moneda === 'BS' ? 'BS' : 'USD';
    const mercado = Number(fp.tasa_usd) > 0 ? Number(fp.tasa_usd) : Number(fp.tasasDia?.mercado) || 0;
    const bcv = Number(fp.tasasDia?.bcv) || null;
    const usdReal = pagoAUsdReal(forma, fp.monto_original, mercado);
    let restanteUsd = (usdReal != null ? usdReal : 0) + (Number(fp.anticipo_aplicado) || 0);
    const lineas = fp.lineas.map((l) => {
      if (restanteUsd <= 0.01) return { ...l, aplicar: '' };
      const enForma = saldoEnForma(saldoVivo(l, bcv), l.moneda, forma, bcv, mercado);
      if (enForma == null || enForma <= 0) return { ...l, aplicar: '' };
      const enUsd = pagoAUsdReal(forma, enForma, mercado) ?? enForma;
      const usar = Math.min(enUsd, restanteUsd);
      const factor = usar / enUsd;
      restanteUsd = Math.round((restanteUsd - usar) * 100) / 100;
      return { ...l, aplicar: String(Math.round(enForma * factor * 100) / 100) };
    });
    return { ...fp, lineas };
  }

  async function guardarPagoRepartido(e) {
    e.preventDefault();
    setErrorPagoRep('');
    if (!formPagoRep.cuenta_id) { setErrorPagoRep('Elegí la cuenta bancaria del pago'); return; }
    const asignaciones = formPagoRep.lineas
      .filter((l) => Number(l.aplicar) > 0)
      .map((l) => ({ factura_id: l.factura_id, monto_original: Number(l.aplicar) }));
    if (asignaciones.length === 0 && !(Number(formPagoRep.monto_original) > 0)) {
      setErrorPagoRep('Cargá un monto y repartilo, o dejá todo como anticipo con un monto.');
      return;
    }
    // Sobra plata y hay facturas que podrían recibirla: casi siempre es un
    // olvido, no un anticipo buscado.
    const sugerido = repartir(formPagoRep).lineas;
    const sinUsar = formPagoRep.lineas.filter((l, i) => (Number(sugerido[i].aplicar) || 0) - (Number(l.aplicar) || 0) > 0.01);
    if (sinUsar.length > 0 && !window.confirm(
      `Parte del pago va a quedar como anticipo y ${sinUsar.length === 1 ? `la factura ${sinUsar[0].numero} sigue` : `${sinUsar.length} facturas siguen`} con saldo.\n` +
      '¿Guardar así? (Cancelar y pulsar "Repartir automático" para aplicarlo a las facturas.)'
    )) return;
    const payload = {
      [CP.param]: parteId,
      fecha: formPagoRep.fecha,
      cuenta_id: formPagoRep.cuenta_id,
      monto_original: Number(formPagoRep.monto_original) || 0,
      tasa_mercado: formPagoRep.tasa_usd || null,
      referencia: formPagoRep.referencia,
      movimiento_id: formPagoRep.movimiento_id,
      anticipo_aplicado: Number(formPagoRep.anticipo_aplicado) || 0,
      asignaciones
    };
    try {
      if (formPagoRep.editandoGrupo) await api.put(`${CP.api}/pago-grupo/${formPagoRep.editandoGrupo}`, payload);
      else await api.post(`${CP.api}/pago`, payload);
      setFormPagoRep(null);
      refrescar();
    } catch (err) {
      setErrorPagoRep(err.response?.data?.error || 'No se pudo registrar el pago');
    }
  }

  // Precarga el modal de "Registrar pago" con los datos de un pago_grupo
  // existente; al guardar, guardarPagoRepartido llama a PUT (revertir y
  // recrear en una sola transacción) en vez de POST.
  async function abrirEditarPagoGrupo(grupo) {
    setErrorPagoRep('');
    const { data } = await api.get(`${CP.api}/pago-grupo/${grupo}`);
    const tasasDia = await traerTasasDia(data.fecha);
    setFormPagoRep({
      editandoGrupo: grupo,
      fecha: data.fecha,
      cuenta_id: data.cuenta_id ? String(data.cuenta_id) : '',
      monto_original: Number(data.monto_original) > 0 ? String(data.monto_original) : '',
      tasa_usd: '',
      referencia: data.referencia || '',
      anticipo_aplicado: Number(data.anticipo_aplicado) > 0 ? String(data.anticipo_aplicado) : '',
      anticipoDisp: Number(data.anticipo_disp) || 0,
      movimiento_id: data.movimiento_id || null,
      movimientoInfo: data.movimientoInfo || null,
      tasasDia,
      lineas: (data.facturas || []).map((f) => ({
        factura_id: f.id, numero: f.numero, fecha: f.fecha, moneda: f.moneda,
        saldo: Number(f.saldo) || 0,
        idx: lineaIndexada(f),
        aplicar: Number(f.aplicar_sugerido) > 0 ? String(f.aplicar_sugerido) : ''
      })),
      // Un pago que no tocó ninguna factura arranca repartiendo solo al
      // cambiarle algo; uno ya repartido respeta lo que se aplicó.
      autoReparto: !(data.facturas || []).some((f) => Number(f.aplicar_sugerido) > 0)
    });
  }

  // Reasignar un anticipo "pendiente de asignar" a una factura: es el mismo
  // flujo de "Registrar pago" (el anticipo es un pozo fungible, no hace
  // falta lógica nueva), sólo que arranca con el anticipo ya cargado.
  async function reasignarAnticipo(g) {
    await abrirPagoRepartido();
    setFormPagoRep((fp) => (fp ? { ...fp, anticipo_aplicado: String(Math.min(g.anticipo_generado, fp.anticipoDisp)) } : fp));
  }

  // Acciones de la pestaña "Pagos": un pago con pago_grupo se edita/borra
  // como grupo completo; uno suelto (una sola factura, sin reparto) es la
  // misma fila `factura_pago` de siempre y reusa el modal simple.
  async function editarPagoDesdeLista(g) {
    if (g.pago_grupo) { await abrirEditarPagoGrupo(g.pago_grupo); return; }
    const facturaId = g.aplicaciones[0]?.factura_id;
    if (!facturaId) return;
    const { data } = await api.get(`${CP.api}/${facturaId}`);
    const pago = data.pagos.find((p) => p.id === g.ids[0]);
    if (pago) abrirEditarPago(data, pago);
  }

  async function eliminarPagoDesdeLista(g) {
    const msg = g.pago_grupo
      ? 'Este pago se registró repartido. Se van a revertir TODAS sus aplicaciones (y el anticipo del sobrante, si hubo). ¿Seguir?'
      : '¿Eliminar este pago?';
    if (!window.confirm(msg)) return;
    try {
      if (g.pago_grupo) await api.delete(`${CP.api}/pago-grupo/${g.pago_grupo}`);
      else await api.delete(`${CP.api}/pagos/${g.ids[0]}`);
      refrescar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar el pago');
    }
  }

  async function abrirEditarPago(factura, pago) {
    setErrorPago('');
    const fecha = String(pago.fecha).slice(0, 10);
    const tasasDia = await traerTasasDia(fecha);
    // Indexada (#151): el pago se convierte según lo pagado ANTES de él.
    const previos = factura.pagos || [];
    const idxPago = previos.findIndex((x) => x.id === pago.id);
    const facturaIdx = esIndexada(factura)
      ? {
          total: factura.total, monto_adeudado: factura.monto_adeudado, tasa_bcv: factura.tasa_bcv,
          pagadoPrevio: previos.slice(0, Math.max(idxPago, 0)).reduce((acc, x) => acc + Number(x.monto_factura), 0)
        }
      : null;
    setFormPago({
      facturaId: factura.id, pagoId: pago.id, fecha,
      facturaMoneda: factura.moneda,
      facturaIdx,
      cuenta_id: pago.cuenta_id ? String(pago.cuenta_id) : '',
      monto_original: String(pago.monto_original),
      tasa_usd: pago.tasa_mercado != null && Number(pago.tasa_mercado) !== Number(tasasDia.mercado) ? String(pago.tasa_mercado) : '',
      tasasDia,
      referencia: pago.referencia || '',
      movimiento_id: pago.movimiento_id || null,
      movimientoInfo: pago.movimiento_id
        ? {
            id: pago.movimiento_id, fecha: pago.movimiento_fecha, monto: pago.movimiento_monto,
            estado: pago.movimiento_estado,
            referencia: pago.movimiento_referencia, descripcion: pago.movimiento_descripcion,
            cuenta_id: pago.cuenta_id, cuenta_nombre: pago.movimiento_cuenta,
            cuenta_moneda: pago.movimiento_cuenta_moneda
          }
        : null
    });
  }

  function cambiarCuentaPago(cuentaId) {
    setFormPago((fp) => ({ ...fp, cuenta_id: cuentaId }));
  }
  async function cambiarFechaPago(fecha) {
    setFormPago((fp) => ({ ...fp, fecha, tasa_usd: '' }));
    const tasasDia = await traerTasasDia(fecha);
    setFormPago((fp) => ({ ...fp, fecha, tasasDia }));
  }

  async function guardarPago(e) {
    e.preventDefault();
    setErrorPago('');
    if (!formPago.cuenta_id) { setErrorPago('Elegí la cuenta bancaria del pago'); return; }
    const payload = {
      fecha: formPago.fecha,
      cuenta_id: formPago.cuenta_id,
      monto_original: formPago.monto_original,
      tasa_mercado: formPago.tasa_usd || null,
      referencia: formPago.referencia,
      movimiento_id: formPago.movimiento_id
    };
    try {
      if (formPago.pagoId) await api.put(`${CP.api}/pagos/${formPago.pagoId}`, payload);
      else await api.post(`${CP.api}/${formPago.facturaId}/pagos`, payload);
      setFormPago(null);
      refrescar();
    } catch (err) {
      setErrorPago(err.response?.data?.error || 'No se pudo registrar el pago');
    }
  }

  async function eliminarPago(pago) {
    const enGrupo = !!pago.pago_grupo;
    const msg = enGrupo
      ? 'Este pago se registró repartido. Se van a revertir TODAS sus aplicaciones (y el anticipo del sobrante, si hubo). ¿Seguir?'
      : '¿Eliminar este pago?';
    if (!window.confirm(msg)) return;
    try {
      if (enGrupo) await api.delete(`${CP.api}/pago-grupo/${pago.pago_grupo}`);
      else await api.delete(`${CP.api}/pagos/${pago.id}`);
      refrescar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar el pago');
    }
  }

  // Al elegir un movimiento en el pop-up: si es de otra cuenta, confirmar
  // antes de cambiar la cuenta del pago (pedido del usuario). Sirve tanto
  // para el pago repartido como para editar un pago suelto.
  function elegirMovimiento(m) {
    const setter = formPagoRep ? setFormPagoRep : setFormPago;
    const actualCuenta = (formPagoRep || formPago)?.cuenta_id;
    if (actualCuenta && String(m.cuenta_id) !== String(actualCuenta)) {
      const cuentaActual = cuentas.find((c) => String(c.id) === String(actualCuenta));
      const ok = window.confirm(
        `El movimiento es de la cuenta "${m.cuenta_nombre}", distinta de la que elegiste` +
        (cuentaActual ? ` ("${cuentaActual.nombre}")` : '') +
        `.\n¿Usar la cuenta "${m.cuenta_nombre}" para este pago?`
      );
      if (!ok) { setPopupVinculo(false); return; }
    }
    if (formPagoRep) guardarPagoRepValor({ movimiento_id: m.id, movimientoInfo: m, cuenta_id: String(m.cuenta_id) });
    else setter((fp) => ({ ...fp, movimiento_id: m.id, movimientoInfo: m, cuenta_id: String(m.cuenta_id) }));
    setPopupVinculo(false);
  }

  const monedaVer = verEn;
  const saldoHeader = resumen
    ? (verEn === 'BCV' ? Number(resumen.saldo_bcv) : Number(resumen.saldo_usd))
    : Number(entidad?.[CP.saldoCol]) || 0;

  return (
    <div>
      <div className="p-4 border-b flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="inline-flex gap-0.5 bg-slate-100 p-0.5 rounded-lg text-xs">
          <button
            onClick={() => setVista('cuenta')}
            className={`px-2.5 py-1 rounded-md font-medium ${vista === 'cuenta' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Estado de cuenta
          </button>
          <button
            onClick={() => setVista('pagos')}
            className={`px-2.5 py-1 rounded-md font-medium ${vista === 'pagos' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Pagos
          </button>
        </div>
        <span className="text-xs text-slate-400">{vista === 'cuenta' ? `${total} ${total === 1 ? CP.doc : CP.docPlural}` : `${pagosTotal} pago${pagosTotal === 1 ? '' : 's'}`}</span>
        <SelectorMoneda vista={verEn} onChange={setVerEn} compacto />
        <div className="ml-auto flex items-center gap-4">
          {resumen && Number(verEn === 'BCV' ? resumen.anticipo_bcv : resumen.anticipo_usd) > 0.02 && (
            <div className="text-right leading-tight">
              <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-400">Anticipo ({verEn})</span>
              <span className="block text-sm font-semibold tabular-nums text-emerald-600">
                {pref(verEn)}{formatMonto(verEn === 'BCV' ? resumen.anticipo_bcv : resumen.anticipo_usd, 2)}
              </span>
            </div>
          )}
          <div className="text-right leading-tight">
            <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              {CP.saldoLabel} ({verEn}){saldoHeader < -0.02 ? ' · a favor' : ''}
            </span>
            <span className={`block text-xl font-bold tabular-nums ${saldoHeader > 0.02 ? 'text-amber-600' : saldoHeader < -0.02 ? 'text-emerald-600' : 'text-slate-800'}`}>
              {pref(verEn)}{formatMonto(Math.abs(saldoHeader), 2)}
            </span>
          </div>
          <button
            onClick={abrirPagoRepartido}
            title="Registrar pago"
            className="bg-emerald-600 hover:bg-emerald-700 text-white p-2 rounded-md"
          >
            <HandCoins size={18} />
          </button>
          {parte === 'cliente' && (
            <button
              onClick={() => setNotaTipo('nota_credito')}
              title="Nota de crédito / débito"
              className="bg-amber-600 hover:bg-amber-700 text-white p-2 rounded-md"
            >
              <FileMinus size={18} />
            </button>
          )}
          {parte === 'proveedor' && onFacturaIA && (
            <BotonFotoFactura onResultado={(r) => onFacturaIA(r)} />
          )}
          <button
            onClick={abrirNuevaFactura}
            title={CP.nueva}
            className="bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-md"
          >
            <Plus size={18} />
          </button>
        </div>
      </div>

      {vista === 'cuenta' && (
        <div className="px-4 py-2.5 border-b bg-slate-50 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
          <label className="flex items-center gap-1.5 text-xs text-slate-500">
            Desde
            <input type="date" value={filtros.desde} onChange={(e) => cambiarFiltro('desde', e.target.value)} className="border rounded-md px-2 py-1 text-sm bg-white" />
          </label>
          <label className="flex items-center gap-1.5 text-xs text-slate-500">
            Hasta
            <input type="date" value={filtros.hasta} onChange={(e) => cambiarFiltro('hasta', e.target.value)} className="border rounded-md px-2 py-1 text-sm bg-white" />
          </label>
          <select value={filtros.estado} onChange={(e) => cambiarFiltro('estado', e.target.value)} className="border rounded-md px-2 py-1 text-sm bg-white">
            <option value="">Todos los estados</option>
            <option value="abiertas">Pendientes (con saldo)</option>
            <option value="pagada">Pagadas</option>
            <option value="anulada">Anuladas</option>
          </select>
          {hayFiltros && (
            <button onClick={() => { setFiltros(FILTROS_VACIOS); setPagina(1); }} className="text-xs text-slate-500 hover:text-primary-600 hover:underline">
              Limpiar
            </button>
          )}
          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-slate-400">Estado de cuenta ({verEn})</span>
            <button onClick={() => descargarEstadoCuenta('excel')} title="Descargar en Excel" className="text-slate-400 hover:text-green-600 transition-colors">
              <FileSpreadsheet size={16} />
            </button>
            <button onClick={() => descargarEstadoCuenta('pdf')} title="Descargar en PDF" className="text-slate-400 hover:text-red-600 transition-colors">
              <FileText size={16} />
            </button>
            <button onClick={() => setEnvioEC({ email: entidad?.email || '', enviando: false, error: '' })} title="Enviar por correo" className="text-slate-400 hover:text-primary-600 transition-colors">
              <Mail size={16} />
            </button>
          </div>
        </div>
      )}

      {vista === 'cuenta' && (
      <div className="overflow-x-auto border-b">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr>
              <th className="p-3 w-6"></th>
              {usaRecurrente && <th className="p-3">Título</th>}
              <th className="p-3">{CP.colNumero}</th>
              <th className="p-3">Fecha</th>
              {usaCanal && <th className="p-3">Canal</th>}
              <th className="p-3">Moneda</th>
              <th className="p-3">Conciliado</th>
              <th className="p-3 text-right">Total ({monedaVer})</th>
              <th className="p-3 text-right">Pagado</th>
              <th className="p-3 text-right">Saldo</th>
            </tr>
          </thead>
          <tbody>
            {cargando ? (
              <tr><td colSpan={8 + (usaCanal ? 1 : 0) + (usaRecurrente ? 1 : 0)} className="p-6 text-center text-slate-400">Cargando...</td></tr>
            ) : facturas.length === 0 ? (
              <tr><td colSpan={8 + (usaCanal ? 1 : 0) + (usaRecurrente ? 1 : 0)} className="p-6 text-center text-slate-400">{hayFiltros ? `No hay ${CP.docPlural} con estos filtros.` : CP.sinFacturas}</td></tr>
            ) : (
              facturas.map((f) => {
                const t = aVista(Number(f.total) + Number(f.monto_iva || 0), verEn, f.moneda, f.tasa_bcv, f.tasa_mercado);
                const pg = aVista(f.pagado, verEn, f.moneda, f.tasa_bcv, f.tasa_mercado);
                // Indexada (#151): lo que se debe hoy, con las tasas de hoy.
                const sd = saldoEnVista(f, verEn, aVista);
                return (
                <React.Fragment key={f.id}>
                  <tr
                    {...filaClickeable(() => toggleExpandir(f))}
                    className={`border-t hover:bg-slate-50 transition-colors cursor-pointer ${expandidaId === f.id ? 'bg-slate-50' : ''} ${FOCO_FILA}`}
                  >
                    <td className="p-3 text-slate-400">{expandidaId === f.id ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</td>
                    {usaRecurrente && (
                      <td className="p-3 whitespace-nowrap text-slate-600">{f.factura_recurrente_nombre || '—'}</td>
                    )}
                    <td className="p-3 font-medium whitespace-nowrap">
                      {f.numero}
                      {f.adjunto_archivo && (
                        <Paperclip size={12} className="ml-1 inline text-slate-400" aria-label="Tiene la factura original adjunta" />
                      )}
                      {f.tipo_documento && f.tipo_documento !== 'factura' && (
                        <span className="ml-1 text-[10px] font-semibold uppercase text-amber-700 bg-amber-50 rounded px-1 py-0.5">
                          {f.tipo_documento === 'nota_credito' ? 'N/C' : 'N/D'}
                        </span>
                      )}
                    </td>
                    <td className="p-3 whitespace-nowrap">{formatFecha(f.fecha)}</td>
                    {usaCanal && <td className="p-3 whitespace-nowrap">{CANAL_LABEL[f.canal] || '—'}</td>}
                    <td className="p-3">
                      <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-500">
                        {MONEDA_LABEL[f.moneda] || f.moneda}
                      </span>
                      {f.indexacion && (
                        <span
                          title="Indexada a BCV: la base se paga a la tasa BCV del día del pago; el IVA y las retenciones quedan fijos en Bs"
                          className="ml-1 text-[10px] font-semibold uppercase text-sky-700 bg-sky-50 rounded px-1 py-0.5"
                        >
                          Indexada
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${ESTADO_BADGE[f.estado]}`}>
                        {ESTADO_LABEL[f.estado]}
                      </span>
                      {Number(f.pagos_sin_vincular) > 0 && f.estado !== 'anulada' && (
                        <span title="Tiene pagos sin conciliar con un movimiento" className="ml-1 inline-flex align-middle text-amber-500">
                          <Unlink size={13} />
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">{pref(verEn)}{formatMonto(t, 2)}</td>
                    <td className="p-3 text-right whitespace-nowrap text-slate-500">{pref(verEn)}{formatMonto(pg, 2)}</td>
                    <td className="p-3 text-right whitespace-nowrap font-medium">{pref(verEn)}{formatMonto(sd, 2)}</td>
                  </tr>
                  {expandidaId === f.id && (
                    <tr className="bg-slate-50/60">
                      <td colSpan={8 + (usaCanal ? 1 : 0) + (usaRecurrente ? 1 : 0)} className="px-4 pb-4">
                        {cargandoDetalle || !detalle ? (
                          <p className="text-slate-400 text-xs py-3">Cargando detalle...</p>
                        ) : (
                          <DetalleFactura
                            factura={f}
                            detalle={detalle}
                            onEditarPago={(p) => abrirEditarPago(f, p)}
                            onEliminarPago={eliminarPago}
                            onEditarFactura={() => abrirEditarFactura(f)}
                            onAnularFactura={() => anularFactura(f)}
                            onImprimirFactura={() => imprimirFactura({ empresaNombre, parteLabel: CP.label, parteNombre: entidad?.nombre, saldoLabel: CP.saldoLabel, factura: f, detalle })}
                            onImprimirPago={(p) => imprimirPago({ empresaNombre, parteLabel: CP.label, parteNombre: entidad?.nombre, factura: f, pago: p })}
                            onImprimirRetencion={(r) => imprimirComprobanteRetencion({ empresa: empresaActivaData, parteNombre: entidad?.nombre, parteRif: entidad?.rif, factura: f, retencion: r })}
                            onDescargarRetencion={parte === 'proveedor' ? (r) => descargarArchivo(`/compras/retenciones/${r.id}/pdf`, `Retencion-${r.numero_comprobante}.pdf`) : null}
                            onEnviarRetencion={parte === 'proveedor' ? (r) => setEnvioRetencion({ retencion: r, email: entidad?.email || '', enviando: false, error: '' }) : null}
                            onDescargarAdjunto={parte === 'proveedor' ? () => verArchivo(`/compras/${f.id}/adjunto/ver`) : null}
                          />
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      )}

      {vista === 'pagos' && (
        <div className="overflow-x-auto border-b">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="p-3 w-6"></th>
                <th className={`p-3 cursor-pointer select-none whitespace-nowrap ${FOCO_FILA}`} {...filaClickeable(() => ordenarPagosPor('fecha'))}>
                  Fecha{pagosOrden.campo === 'fecha' ? (pagosOrden.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
                <th className={`p-3 cursor-pointer select-none whitespace-nowrap ${FOCO_FILA}`} {...filaClickeable(() => ordenarPagosPor('cuenta'))}>
                  Cuenta{pagosOrden.campo === 'cuenta' ? (pagosOrden.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
                <th className={`p-3 text-right cursor-pointer select-none whitespace-nowrap ${FOCO_FILA}`} {...filaClickeable(() => ordenarPagosPor('monto'))}>
                  Monto del pago{pagosOrden.campo === 'monto' ? (pagosOrden.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
                <th className="p-3">Facturas cubiertas</th>
                <th className={`p-3 cursor-pointer select-none whitespace-nowrap ${FOCO_FILA}`} {...filaClickeable(() => ordenarPagosPor('conciliacion'))}>
                  Conciliación{pagosOrden.campo === 'conciliacion' ? (pagosOrden.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
                <th className="p-3 w-28"></th>
              </tr>
            </thead>
            <tbody>
              {pagosCargando ? (
                <tr><td colSpan={7} className="p-6 text-center text-slate-400">Cargando...</td></tr>
              ) : pagos.length === 0 ? (
                <tr><td colSpan={7} className="p-6 text-center text-slate-400">Todavía no hay pagos registrados.</td></tr>
              ) : (
                pagos.map((g) => {
                  const clave = g.pago_grupo || `solo-${g.ids[0]}`;
                  const abierto = pagoExpandidoId === clave;
                  return (
                    <React.Fragment key={clave}>
                      <tr
                        {...filaClickeable(() => setPagoExpandidoId(abierto ? null : clave))}
                        className={`border-t hover:bg-slate-50 transition-colors cursor-pointer ${abierto ? 'bg-slate-50' : ''} ${FOCO_FILA}`}
                      >
                        <td className="p-3 text-slate-400">{abierto ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</td>
                        <td className="p-3 whitespace-nowrap">{formatFecha(g.fecha)}</td>
                        <td className="p-3 whitespace-nowrap text-slate-500">{g.cuenta_nombre || '—'}</td>
                        <td className="p-3 text-right whitespace-nowrap font-medium">$ {formatMonto(g.monto_usd, 2)}</td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-1">
                            {g.aplicaciones.length === 0 ? (
                              <span className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-700">Anticipo · pendiente de asignar</span>
                            ) : g.aplicaciones.map((a) => (
                              <span key={a.factura_id} className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                                {a.numero} · {pref(a.factura_moneda)}{formatMonto(a.monto_factura)}
                              </span>
                            ))}
                            {g.anticipo_generado > 0.02 && g.aplicaciones.length > 0 && (
                              <span className="text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">+ anticipo $ {formatMonto(g.anticipo_generado, 2)}</span>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          {!g.cuenta_id ? (
                            <span className="text-xs text-slate-400">—</span>
                          ) : (
                            <BadgeVinculoPago movimientoId={g.movimiento_id} estado={g.movimiento_estado} />
                          )}
                        </td>
                        <td className="p-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          {g.pendiente_asignar && (
                            <button onClick={() => reasignarAnticipo(g)} title="Reasignar a una factura" className="text-slate-400 hover:text-emerald-600 mr-2">
                              <HandCoins size={13} />
                            </button>
                          )}
                          <button
                            onClick={() => imprimirPagoGrupo({ empresaNombre, parteLabel: CP.label, parteNombre: entidad?.nombre, pago: g })}
                            title="Imprimir recibo" className="text-slate-400 hover:text-primary-600 mr-2"
                          >
                            <Printer size={13} />
                          </button>
                          <button onClick={() => editarPagoDesdeLista(g)} title="Editar pago" className="text-slate-400 hover:text-primary-600 mr-2">
                            <Pencil size={13} />
                          </button>
                          <button onClick={() => eliminarPagoDesdeLista(g)} title="Eliminar pago" className="text-slate-400 hover:text-red-600">
                            <Trash2 size={13} />
                          </button>
                        </td>
                      </tr>
                      {abierto && (
                        <tr className="bg-slate-50/60">
                          <td colSpan={7} className="px-4 pb-4">
                            <div className="text-xs bg-white border rounded-md p-3 space-y-1">
                              {g.aplicaciones.map((a) => (
                                <div key={a.factura_id} className="flex justify-between">
                                  <span>Factura #{a.numero}</span>
                                  <span className="tabular-nums font-medium">{pref(a.factura_moneda)}{formatMonto(a.monto_factura)}</span>
                                </div>
                              ))}
                              {g.anticipo_generado > 0.02 && (
                                <div className="flex justify-between text-emerald-700">
                                  <span>Anticipo generado (sobrante)</span>
                                  <span className="tabular-nums font-medium">$ {formatMonto(g.anticipo_generado, 2)}</span>
                                </div>
                              )}
                              {g.anticipo_consumido > 0.02 && (
                                <div className="flex justify-between text-slate-500">
                                  <span>Anticipo aplicado (saldo a favor)</span>
                                  <span className="tabular-nums font-medium">$ {formatMonto(g.anticipo_consumido, 2)}</span>
                                </div>
                              )}
                              {g.referencia && (
                                <div className="flex justify-between text-slate-500 pt-1 border-t">
                                  <span>Referencia</span>
                                  <span>{g.referencia}</span>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <Paginacion
        pagina={vista === 'cuenta' ? pagina : pagosPagina}
        totalPaginas={vista === 'cuenta' ? totalPaginas : pagosTotalPaginas}
        total={vista === 'cuenta' ? total : pagosTotal}
        onCambiarPagina={vista === 'cuenta' ? setPagina : setPagosPagina}
      />

      {anticipos.length > 0 && (
        <div className="border-b bg-emerald-50/40">
          <div className="px-4 pt-3 pb-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Anticipos</span>
          </div>
          <table className="w-full text-xs">
            <thead className="text-slate-500 text-left">
              <tr>
                <th className="px-4 py-1">Fecha</th>
                <th className="px-2 py-1">Cuenta / referencia</th>
                <th className="px-2 py-1 text-right">Monto</th>
                <th className="px-4 py-1 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {anticipos.map((a) => {
                const usd = Number(a.monto_usd);
                const esConsumo = usd < 0;
                return (
                  <tr key={a.id} className="border-t border-emerald-100">
                    <td className="px-4 py-1.5 whitespace-nowrap">{formatFecha(a.fecha)}</td>
                    <td className="px-2 py-1.5 text-slate-500">
                      {esConsumo ? 'Aplicado a facturas' : (a.cuenta_nombre || '—')}
                      {a.referencia && !esConsumo ? ` · ${a.referencia}` : ''}
                    </td>
                    <td className={`px-2 py-1.5 text-right tabular-nums font-medium ${esConsumo ? 'text-slate-500' : 'text-emerald-700'}`}>
                      {esConsumo ? '−' : '+'}$ {formatMonto(Math.abs(usd), 2)}
                    </td>
                    <td className="px-4 py-1.5 text-right">
                      <button onClick={() => eliminarAnticipo(a)} className="text-slate-400 hover:text-red-600" title="Eliminar anticipo">
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* La factura de COMPRA se sigue cargando en un modal; la de venta
          tiene pantalla propia (ver FacturaVentaForm). Mismo formulario. */}
      {formFactura && (
        <Modal
          title={formFactura.facturaId ? `Editar ${CP.doc}` : CP.nueva}
          onClose={() => setFormFactura(null)}
          maxWidth="max-w-3xl"
        >
          <FacturaForm
            parte={parte}
            entidad={entidad}
            facturaId={formFactura.facturaId}
            esRestaurante={esRestaurante}
            extraccion={formFactura.extraccion || null}
            onGuardado={(id) => {
              // Tras una carga por IA se abre la factura recién creada para
              // que se vean sus retenciones (PDF / correo) de una vez.
              const vieneDeIA = !!formFactura.extraccion;
              setFormFactura(null);
              refrescar();
              if (vieneDeIA && id) toggleExpandir({ id });
            }}
            onCancelar={() => setFormFactura(null)}
          />
        </Modal>
      )}

      {envioEC && (
        <Modal title="Enviar estado de cuenta" onClose={() => setEnvioEC(null)} maxWidth="max-w-md">
          <form onSubmit={enviarEstadoCuenta} className="space-y-3">
            <p className="text-sm text-slate-600">
              Estado de cuenta de <strong>{entidad?.nombre}</strong> en PDF ({verEn}
              {filtros.desde || filtros.hasta ? `, ${filtros.desde ? `del ${formatFecha(filtros.desde)} ` : ''}${filtros.hasta ? `al ${formatFecha(filtros.hasta)}` : 'a hoy'}` : ''}
              {filtros.estado ? ', con el filtro de estado aplicado' : ''}), adjunto a un correo.
            </p>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Enviar a</label>
              <input
                type="email" required autoFocus
                value={envioEC.email}
                onChange={(e) => setEnvioEC({ ...envioEC, email: e.target.value })}
                className="border rounded-md px-3 py-2 w-full text-sm"
              />
              {!entidad?.email && <p className="text-[11px] text-slate-400 mt-1">El {CP.label.toLowerCase()} no tenía correo: se le guarda este.</p>}
            </div>
            {envioEC.error && <p className="text-red-600 text-sm">{envioEC.error}</p>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setEnvioEC(null)} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100">
                Cancelar
              </button>
              <button type="submit" disabled={envioEC.enviando} className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm hover:bg-primary-700 disabled:opacity-60">
                {envioEC.enviando ? 'Enviando…' : 'Enviar'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {envioRetencion && (
        <Modal title={`Enviar ${TIPO_RETENCION_LABEL[envioRetencion.retencion.tipo] || 'retención'}`} onClose={() => setEnvioRetencion(null)} maxWidth="max-w-md">
          <form onSubmit={enviarRetencion} className="space-y-3">
            <p className="text-sm text-slate-600">
              Comprobante N° <strong>{envioRetencion.retencion.numero_comprobante}</strong> en PDF, adjunto a un correo para {entidad?.nombre}.
            </p>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Enviar a</label>
              <input
                type="email" required autoFocus
                value={envioRetencion.email}
                onChange={(e) => setEnvioRetencion({ ...envioRetencion, email: e.target.value })}
                className="border rounded-md px-3 py-2 w-full text-sm"
              />
              {!entidad?.email && <p className="text-[11px] text-slate-400 mt-1">El proveedor no tenía correo: se le guarda este.</p>}
            </div>
            {envioRetencion.error && <p className="text-red-600 text-sm">{envioRetencion.error}</p>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setEnvioRetencion(null)} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100">
                Cancelar
              </button>
              <button type="submit" disabled={envioRetencion.enviando} className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm hover:bg-primary-700 disabled:opacity-60">
                {envioRetencion.enviando ? 'Enviando…' : 'Enviar'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {notaTipo && (
        <Modal title="Nota de crédito / débito" onClose={() => setNotaTipo(null)} maxWidth="max-w-lg">
          <div className="space-y-3">
            <div className="flex gap-2">
              {['nota_credito', 'nota_debito'].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setNotaTipo(t)}
                  className={`px-3 py-1.5 rounded-md text-sm border ${
                    notaTipo === t ? 'bg-primary-600 text-white border-primary-600' : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {TIPO_DOCUMENTO_LABEL[t]}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500">
              Una nota siempre se hace sobre una factura anterior: elegí cuál afecta y el formulario abre con
              sus líneas precargadas para ajustar solo lo que cambia.
            </p>
            <div className="max-h-80 overflow-y-auto border rounded-md divide-y">
              {facturas.filter((f) => f.tipo_documento === 'factura' || !f.tipo_documento).length === 0 && (
                <p className="p-4 text-sm text-slate-400 text-center">Este cliente todavía no tiene facturas.</p>
              )}
              {facturas
                .filter((f) => (f.tipo_documento === 'factura' || !f.tipo_documento) && f.estado !== 'anulada')
                .map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => abrirNota(TIPO_NOTA_RUTA[notaTipo], f)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between gap-3"
                  >
                    <span>
                      <strong className="text-slate-700">{f.numero}</strong>
                      <span className="text-slate-400"> · {formatFecha(f.fecha)}</span>
                    </span>
                    <span className="text-slate-600 tabular-nums">
                      {pref(f.moneda)}{formatMonto(Number(f.total) + Number(f.monto_iva || 0))}
                    </span>
                  </button>
                ))}
            </div>
          </div>
        </Modal>
      )}

      {formPago && (
        <Modal
          title={formPago.pagoId ? 'Editar pago' : 'Registrar pago'}
          onClose={() => { setFormPago(null); setPopupVinculo(false); }}
          maxWidth="max-w-lg"
        >
          <form onSubmit={guardarPago} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs text-slate-500 mb-1">{CP.pagoCuentaLabel}</label>
                <select
                  value={formPago.cuenta_id}
                  onChange={(e) => cambiarCuentaPago(e.target.value)}
                  className="border rounded-md px-2 py-1.5 w-full text-sm"
                >
                  <option value="">Elegir cuenta…</option>
                  {cuentas.map((c) => (
                    <option key={c.id} value={c.id}>{c.nombre} ({MONEDA_LABEL[c.moneda] || c.moneda})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Fecha</label>
                <input
                  type="date" value={formPago.fecha}
                  onChange={(e) => cambiarFechaPago(e.target.value)}
                  className="border rounded-md px-2 py-1.5 w-full text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Monto ({pagoEnBs ? 'Bs' : 'USD'})</label>
                <input
                  type="number" step="0.01" min="0" value={formPago.monto_original}
                  onChange={(e) => setFormPago({ ...formPago, monto_original: e.target.value })}
                  className="border rounded-md px-2 py-1.5 w-full text-sm"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-slate-500 mb-1">Referencia (opcional)</label>
                <input
                  value={formPago.referencia}
                  onChange={(e) => setFormPago({ ...formPago, referencia: e.target.value })}
                  className="border rounded-md px-2 py-1.5 w-full text-sm"
                />
              </div>
            </div>

            <div className="bg-slate-50 rounded-md p-3 text-xs space-y-1">
              <p className="text-slate-600">
                Se aplica a la factura:{' '}
                <strong>{pref(monedaFacturaPago)}{pagoAplicado != null ? formatMonto(pagoAplicado, 2) : '—'}</strong>
                {formPago.facturaIdx && bcvPago > 0 && (
                  <span className="text-slate-400"> (Bs de emisión: factura indexada, BCV del pago {formatMonto(bcvPago, 2)})</span>
                )}
                {pagoEnBs && monedaFacturaPago === 'BCV' && bcvPago > 0 && (
                  <span className="text-slate-400"> (Bs ÷ tasa BCV {formatMonto(bcvPago, 2)})</span>
                )}
                {pagoEnBs && monedaFacturaPago === 'USD' && mercadoPago > 0 && (
                  <span className="text-slate-400"> (Bs ÷ tasa USD {formatMonto(mercadoPago, 2)})</span>
                )}
              </p>
              <p className="text-slate-400">
                ≈ USD $ {pagoUsdReal != null ? formatMonto(pagoUsdReal, 2) : '—'}
              </p>
              {pagoEnBs && monedaFacturaPago !== 'BCV' && (
                Number(mercadoPago) > 0 ? (
                  <div className="flex items-center gap-2 pt-1">
                    <label className="text-slate-500 whitespace-nowrap">Ajustar tasa USD (opcional)</label>
                    <input
                      type="number" step="0.0001" min="0" value={formPago.tasa_usd}
                      placeholder={formatMonto(mercadoPago, 2)}
                      onChange={(e) => setFormPago({ ...formPago, tasa_usd: e.target.value })}
                      className="border rounded-md px-2 py-1 w-32 text-sm"
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-2 pt-1">
                    <label className="text-amber-600 whitespace-nowrap">Esa fecha no tiene tasa: escribí la tasa USD</label>
                    <input
                      type="number" step="0.0001" min="0" value={formPago.tasa_usd}
                      onChange={(e) => setFormPago({ ...formPago, tasa_usd: e.target.value })}
                      className="border rounded-md px-2 py-1 w-32 text-sm"
                    />
                  </div>
                )
              )}
              {pagoEnBs && monedaFacturaPago === 'BCV' && !(bcvPago > 0) && (
                <p className="text-amber-600">No hay tasa BCV cargada para el {formatFecha(formPago.fecha)} (fecha del pago). Cargala en Tasas de Cambio para esa fecha y volvé a guardar el pago.</p>
              )}
            </div>

            <div className="rounded-md border p-3 bg-slate-50">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-500">Movimiento bancario</span>
                {!formPago.movimiento_id && parte === 'cliente' ? (
                  <span className="inline-flex items-center gap-1 text-slate-500 text-xs"><Link2 size={13} /> Se genera al guardar</span>
                ) : (
                  <BadgeVinculoPago movimientoId={formPago.movimiento_id} estado={formPago.movimientoInfo?.estado} />
                )}
              </div>
              {formPago.movimientoInfo ? (
                <div className="text-xs text-slate-600 mt-2">
                  {formatFecha(formPago.movimientoInfo.fecha)} · {formPago.movimientoInfo.cuenta_nombre} ·{' '}
                  {pref(formPago.movimientoInfo.cuenta_moneda)}{formatMonto(formPago.movimientoInfo.monto)}
                  {formPago.movimientoInfo.referencia ? ` · ref ${formPago.movimientoInfo.referencia}` : ''}
                </div>
              ) : parte === 'cliente' ? (
                <p className="text-xs text-slate-400 mt-2">Se crea un movimiento de ingreso con estos datos al guardar el pago.</p>
              ) : (
                <p className="text-xs text-slate-400 mt-2">Todavía no se asoció a ningún movimiento.</p>
              )}
              <div className="flex gap-2 mt-2">
                <button type="button" onClick={() => setPopupVinculo(true)} className="text-primary-600 text-xs inline-flex items-center gap-1 hover:underline">
                  <Search size={13} /> {formPago.movimiento_id ? 'Cambiar' : parte === 'cliente' ? 'Vincular a uno existente' : 'Buscar y vincular'}
                </button>
                {formPago.movimiento_id && (
                  <button
                    type="button"
                    onClick={() => setFormPago({ ...formPago, movimiento_id: null, movimientoInfo: null })}
                    className="text-slate-500 text-xs inline-flex items-center gap-1 hover:underline"
                  >
                    <Unlink size={13} /> Quitar vínculo
                  </button>
                )}
              </div>
            </div>

            {errorPago && <p className="text-red-600 text-sm">{errorPago}</p>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setFormPago(null)} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100">
                Cancelar
              </button>
              <button type="submit" className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm hover:bg-primary-700">
                {formPago.pagoId ? 'Guardar' : 'Registrar pago'}
              </button>
            </div>
          </form>

          {popupVinculo && (
            <PopupVincularMovimiento
              apiBase={CP.api}
              movLabel={CP.movLabel}
              montoSugerido={formPago.monto_original}
              fechaSugerida={formPago.fecha}
              nombreParte={entidad?.nombre}
              clienteId={parte === 'cliente' ? entidad?.id : undefined}
              pagoActual={formPago.pagoId}
              cuentaId={formPago.cuenta_id}
              monedaCuenta={cuentas.find((c) => String(c.id) === String(formPago.cuenta_id))?.moneda}
              onCerrar={() => setPopupVinculo(false)}
              onElegir={elegirMovimiento}
            />
          )}
        </Modal>
      )}

      {formPagoRep && (() => {
        const cta = cuentas.find((c) => String(c.id) === String(formPagoRep.cuenta_id));
        const forma = cta?.moneda === 'BS' ? 'BS' : 'USD';
        const mercado = Number(formPagoRep.tasa_usd) > 0 ? Number(formPagoRep.tasa_usd) : Number(formPagoRep.tasasDia?.mercado) || 0;
        const bcvRep = Number(formPagoRep.tasasDia?.bcv) || null;
        const efUsd = pagoAUsdReal(forma, formPagoRep.monto_original, mercado) ?? 0;
        const antApl = Number(formPagoRep.anticipo_aplicado) || 0;
        // Misma tasa para todo el pago (manual del pago, si no la del día):
        // sin eso, dos facturas convertidas a tasas distintas "cuadran" en
        // USD pero no son el mismo billete, y queda un sobrante/faltante
        // que no es plata real.
        const aplicadoUsd = formPagoRep.lineas.reduce((s, l) => {
          const v = Number(l.aplicar) || 0;
          const u = pagoAUsdReal(forma, v, mercado);
          return s + (u != null ? u : v);
        }, 0);
        const sobrante = Math.round((efUsd + antApl - aplicadoUsd) * 100) / 100;
        const pm = forma === 'BS' ? 'Bs ' : '$ ';
        // Un solo campo de tasa arriba: si hay alguna factura en BCV, manda esa
        // (fija, oficial) por sobre la de mercado -- asume que el pago es a una
        // factura (o a varias con la misma moneda), que es el caso de siempre.
        const usaBcv = formPagoRep.lineas.some((l) => l.moneda === 'BCV' || l.idx);
        const faltaBcvDelDia = (forma === 'BS' || formPagoRep.lineas.some((l) => l.idx)) && usaBcv && !bcvRep;
        return (
          <Modal title={formPagoRep.editandoGrupo ? 'Editar pago' : 'Registrar pago'} onClose={() => { setFormPagoRep(null); setPopupVinculo(false); }} maxWidth="max-w-2xl">
            <form onSubmit={guardarPagoRepartido} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs text-slate-500 mb-1">{CP.pagoCuentaLabel}</label>
                  <select
                    value={formPagoRep.cuenta_id}
                    onChange={(e) => guardarPagoRepValor({ cuenta_id: e.target.value })}
                    className="border rounded-md px-2 py-1.5 w-full text-sm"
                  >
                    <option value="">Elegir cuenta…</option>
                    {cuentas.map((c) => (
                      <option key={c.id} value={c.id}>{c.nombre} ({MONEDA_LABEL[c.moneda] || c.moneda})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Fecha</label>
                  <input
                    type="date" value={formPagoRep.fecha}
                    onChange={async (e) => { const f = e.target.value; guardarPagoRepValor({ fecha: f, tasa_usd: '' }); const td = await traerTasasDia(f); guardarPagoRepValor({ fecha: f, tasasDia: td }); }}
                    className="border rounded-md px-2 py-1.5 w-full text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Monto de la transacción ({forma === 'BS' ? 'Bs' : 'USD'})</label>
                  <input
                    type="number" step="0.01" min="0" value={formPagoRep.monto_original}
                    onChange={(e) => guardarPagoRepValor({ monto_original: e.target.value })}
                    className="border rounded-md px-2 py-1.5 w-full text-sm"
                  />
                </div>
                {forma === 'BS' && usaBcv && (
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">
                      Tasa BCV del día {bcvRep > 0 ? '' : <span className="text-amber-600">(no cargada todavía)</span>}
                    </label>
                    <input
                      type="text" readOnly disabled
                      value={bcvRep > 0 ? formatMonto(bcvRep, 2) : 'sin tasa ese día'}
                      className="border rounded-md px-2 py-1.5 w-full text-sm bg-slate-50 text-slate-500"
                    />
                  </div>
                )}
                {forma === 'BS' && !usaBcv && (
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">
                      Tasa USD {mercado > 0 ? '(opcional)' : <span className="text-amber-600">(esa fecha no tiene, escribila)</span>}
                    </label>
                    <input
                      type="number" step="0.0001" min="0" value={formPagoRep.tasa_usd}
                      placeholder={mercado > 0 ? formatMonto(mercado, 2) : ''}
                      onChange={(e) => guardarPagoRepValor({ tasa_usd: e.target.value })}
                      className="border rounded-md px-2 py-1.5 w-full text-sm"
                    />
                  </div>
                )}
                {formPagoRep.anticipoDisp > 0.02 && (
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Usar anticipo disponible (USD $ {formatMonto(formPagoRep.anticipoDisp, 2)})</label>
                    <input
                      type="number" step="0.01" min="0" max={formPagoRep.anticipoDisp} value={formPagoRep.anticipo_aplicado}
                      onChange={(e) => guardarPagoRepValor({ anticipo_aplicado: e.target.value })}
                      className="border rounded-md px-2 py-1.5 w-full text-sm"
                    />
                  </div>
                )}
                <div className="col-span-2">
                  <label className="block text-xs text-slate-500 mb-1">Referencia (opcional)</label>
                  <input
                    value={formPagoRep.referencia}
                    onChange={(e) => guardarPagoRepValor({ referencia: e.target.value })}
                    className="border rounded-md px-2 py-1.5 w-full text-sm"
                  />
                </div>
              </div>

              <div className="rounded-md border p-3 bg-slate-50">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500">Movimiento bancario</span>
                  {!formPagoRep.movimiento_id && parte === 'cliente' ? (
                    <span className="inline-flex items-center gap-1 text-slate-500 text-xs"><Link2 size={13} /> Se genera al guardar</span>
                  ) : (
                    <BadgeVinculoPago movimientoId={formPagoRep.movimiento_id} estado={formPagoRep.movimientoInfo?.estado} />
                  )}
                </div>
                {formPagoRep.movimientoInfo ? (
                  <div className="text-xs text-slate-600 mt-2">
                    {formatFecha(formPagoRep.movimientoInfo.fecha)} · {formPagoRep.movimientoInfo.cuenta_nombre} ·{' '}
                    {pref(formPagoRep.movimientoInfo.cuenta_moneda)}{formatMonto(formPagoRep.movimientoInfo.monto)}
                  </div>
                ) : parte === 'cliente' ? (
                  <p className="text-xs text-slate-400 mt-2">Se crea un movimiento de ingreso con estos datos al guardar el pago.</p>
                ) : <p className="text-xs text-slate-400 mt-2">Todavía no se asoció a ningún movimiento.</p>}
                <div className="flex gap-2 mt-2">
                  <button type="button" onClick={() => setPopupVinculo(true)} className="text-primary-600 text-xs inline-flex items-center gap-1 hover:underline">
                    <Search size={13} /> {formPagoRep.movimiento_id ? 'Cambiar' : parte === 'cliente' ? 'Vincular a uno existente' : 'Buscar y vincular'}
                  </button>
                  {formPagoRep.movimiento_id && (
                    <button type="button" onClick={() => guardarPagoRepValor({ movimiento_id: null, movimientoInfo: null })} className="text-slate-500 text-xs inline-flex items-center gap-1 hover:underline">
                      <Unlink size={13} /> Quitar vínculo
                    </button>
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-slate-500">Aplicar a facturas</span>
                  <button type="button" onClick={repartirAuto} className="text-primary-600 text-xs hover:underline">Repartir automático</button>
                </div>
                {faltaBcvDelDia && (
                  <p className="text-amber-600 text-xs mb-1">
                    No hay tasa BCV cargada para el {formatFecha(formPagoRep.fecha)}: no se puede calcular cuántos Bs corresponden a las facturas en BCV.
                    Cargala en Tasas de Cambio para esa fecha (apenas se publique) y volvé a entrar acá.
                  </p>
                )}
                {formPagoRep.lineas.length === 0 ? (
                  <p className="text-xs text-slate-400">No hay facturas con saldo. Todo el monto quedará como anticipo.</p>
                ) : (
                  <div className="border rounded-md overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-slate-500 text-left">
                        <tr>
                          <th className="p-2">{CP.colNumero}</th>
                          <th className="p-2">Fecha</th>
                          <th className="p-2 text-right">Saldo</th>
                          <th className="p-2 text-right w-32">Aplicar ({forma === 'BS' ? 'Bs' : 'USD'})</th>
                          <th className="p-2 text-right">Saldo restante</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formPagoRep.lineas.map((l, i) => {
                          // Indexada (#151): su saldo en Bs depende de la BCV de la fecha del pago.
                          const saldoL = saldoVivo(l, bcvRep);
                          const enForma = saldoEnForma(saldoL, l.moneda, forma, bcvRep, mercado);
                          // Saldo restante = la factura con la tasa de HOY menos lo que
                          // se le está aplicando con esa misma tasa -- la previsualización
                          // real de cómo queda, no la que quedó fija al crear la factura.
                          const montoAplicado = pagoAMonedaFactura(forma, Number(l.aplicar) || 0, l.moneda, bcvRep, mercado);
                          const restante = montoAplicado != null ? Math.max(round2(saldoL - montoAplicado), 0) : null;
                          return (
                            <tr key={l.factura_id} className="border-t">
                              <td className="p-2 font-medium whitespace-nowrap">{l.numero}</td>
                              <td className="p-2 whitespace-nowrap">{formatFecha(l.fecha)}</td>
                              <td className="p-2 text-right whitespace-nowrap text-slate-500">
                                {pref(l.moneda)}{formatMonto(saldoL, 2)}
                                {l.idx && <span className="ml-1 text-[10px] font-semibold uppercase text-sky-700">indexada</span>}
                                {enForma != null && l.moneda !== forma && <span className="text-slate-400"> · ≈{pm}{formatMonto(enForma, 2)}</span>}
                                {enForma == null && l.moneda === 'BCV' && forma === 'BS' && (
                                  <span className="text-amber-600"> · sin tasa BCV para el {formatFecha(formPagoRep.fecha)}</span>
                                )}
                              </td>
                              <td className="p-2 text-right">
                                <input
                                  type="number" step="0.01" min="0" value={l.aplicar}
                                  onChange={(e) => setFormPagoRep((fp) => ({ ...fp, autoReparto: false, lineas: fp.lineas.map((x, j) => j === i ? { ...x, aplicar: e.target.value } : x) }))}
                                  className="border rounded px-1.5 py-1 w-28 text-right text-sm"
                                />
                              </td>
                              <td className="p-2 text-right whitespace-nowrap font-medium">
                                {restante != null ? `${pref(l.moneda)}${formatMonto(restante, 2)}` : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="bg-slate-50 rounded-md p-3 text-xs space-y-0.5">
                <p className="text-slate-600">Efectivo ≈ USD $ {formatMonto(efUsd, 2)}{antApl > 0 && <> · Anticipo aplicado $ {formatMonto(antApl, 2)}</>}</p>
                <p className="text-slate-600">Aplicado a facturas ≈ USD $ {formatMonto(aplicadoUsd, 2)}</p>
                <p className={sobrante < -0.02 ? 'text-red-600 font-medium' : 'text-emerald-700 font-medium'}>
                  {sobrante < -0.02
                    ? `Asignaste $ ${formatMonto(-sobrante, 2)} de más`
                    : `Sobrante → anticipo: USD $ ${formatMonto(Math.max(sobrante, 0), 2)}`}
                </p>
              </div>

              {errorPagoRep && <p className="text-red-600 text-sm">{errorPagoRep}</p>}
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setFormPagoRep(null)} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancelar</button>
                <button type="submit" disabled={sobrante < -0.02} className="bg-emerald-600 disabled:opacity-50 text-white px-4 py-2 rounded-md text-sm hover:bg-emerald-700">{formPagoRep.editandoGrupo ? 'Guardar' : 'Registrar pago'}</button>
              </div>
            </form>

            {popupVinculo && (
              <PopupVincularMovimiento
                apiBase={CP.api}
                movLabel={CP.movLabel}
                montoSugerido={formPagoRep.monto_original}
                fechaSugerida={formPagoRep.fecha}
                nombreParte={entidad?.nombre}
                clienteId={parte === 'cliente' ? entidad?.id : undefined}
                cuentaId={formPagoRep.cuenta_id}
                monedaCuenta={cta?.moneda}
                onCerrar={() => setPopupVinculo(false)}
                onElegir={elegirMovimiento}
              />
            )}
          </Modal>
        );
      })()}
    </div>
  );
}

// ---------- Mini-modal para el alta rápida de producto / categoría ----------
// Se monta encima del formulario de factura (z mayor) y NO usa <form> anidado
// en el DOM porque va como hermano del <form> de la factura.
// ---------- Detalle de factura expandida ----------
function DetalleFactura({
  factura, detalle, onEditarPago, onEliminarPago,
  onEditarFactura, onAnularFactura, onImprimirFactura, onImprimirPago, onImprimirRetencion,
  onDescargarRetencion, onEnviarRetencion, onDescargarAdjunto
}) {
  const anulada = detalle.estado === 'anulada';
  const pm = pref(detalle.moneda);
  return (
    <div className="bg-white rounded-md border mt-2 p-3 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-slate-400">
          Creada {formatFechaDeMarca(detalle.creado_en)} · Moneda {MONEDA_LABEL[detalle.moneda] || detalle.moneda}
          {detalle.notas ? ` · ${detalle.notas}` : ''}
        </span>
        <div className="flex items-center gap-3">
          {onDescargarAdjunto && detalle.adjunto_archivo && (
            <button onClick={onDescargarAdjunto} className="flex items-center gap-1 text-xs text-slate-500 hover:text-primary-600 transition-colors" title={`Ver la factura original (${detalle.adjunto_nombre || 'archivo'}) en otra pestaña`}>
              <Paperclip size={15} /> Factura original
            </button>
          )}
          {(detalle.retenciones || []).map((r) => (
            <button
              key={r.tipo}
              onClick={() => onImprimirRetencion(r)}
              className="text-slate-400 hover:text-primary-600 transition-colors"
              title={`Imprimir ${TIPO_RETENCION_LABEL[r.tipo] || 'comprobante de retención'}`}
            >
              <Receipt size={15} />
            </button>
          ))}
          <button onClick={onImprimirFactura} className="text-slate-400 hover:text-primary-600 transition-colors" title="Imprimir factura">
            <Printer size={15} />
          </button>
          {!anulada && (
            <button onClick={onEditarFactura} className="text-slate-400 hover:text-primary-600 transition-colors" title="Editar factura">
              <Pencil size={15} />
            </button>
          )}
          {!anulada && (
            <button onClick={onAnularFactura} className="text-slate-400 hover:text-amber-600 transition-colors" title="Anular factura">
              <Ban size={15} />
            </button>
          )}
        </div>
      </div>

      <div>
        <p className="text-xs font-medium text-slate-500 mb-1">Líneas</p>
        <table className="w-full text-xs">
          <thead className="text-slate-400 text-left">
            <tr>
              <th className="py-1">Descripción</th>
              <th className="py-1">Categoría</th>
              <th className="py-1 text-right">Cant.</th>
              <th className="py-1 text-right">Precio</th>
              <th className="py-1 text-right">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {detalle.lineas.map((l) => (
              <tr key={l.id} className="border-t">
                <td className="py-1">
                  {l.descripcion}{l.producto_nombre ? '' : ' *'}
                  {!!l.exento && <span className="ml-1 text-[10px] text-slate-400 uppercase">exento</span>}
                </td>
                <td className="py-1 text-slate-500">{l.categoria_nombre || (l.producto_nombre ? '—' : '')}</td>
                <td className="py-1 text-right">{formatMonto(l.cantidad)}</td>
                <td className="py-1 text-right">{pm}{formatMonto(l.precio_unitario)}</td>
                <td className="py-1 text-right">{pm}{formatMonto(l.subtotal)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t font-medium">
              <td className="py-1" colSpan={4}>Total (base)</td>
              <td className="py-1 text-right">{pm}{formatMonto(detalle.total)}</td>
            </tr>
            {Number(detalle.monto_iva) > 0 && (
              <tr>
                <td className="py-1 text-slate-500" colSpan={4}>IVA</td>
                <td className="py-1 text-right text-slate-500">{pm}{formatMonto(detalle.monto_iva)}</td>
              </tr>
            )}
            {Number(detalle.monto_iva) > 0 && (
              <tr className="font-medium">
                <td className="py-1" colSpan={4}>Total c/IVA</td>
                <td className="py-1 text-right">{pm}{formatMonto(Number(detalle.total) + Number(detalle.monto_iva))}</td>
              </tr>
            )}
            {(detalle.retenciones || []).map((r) => (
              <tr key={r.tipo}>
                <td className="py-1 text-slate-500" colSpan={4}>
                  <span className="inline-flex items-center gap-2">
                    {TIPO_RETENCION_LABEL[r.tipo] || r.tipo}{r.islr_concepto ? ` (${r.islr_concepto})` : ''}
                    {r.numero_comprobante && <span className="text-slate-400">N° {r.numero_comprobante}</span>}
                    {onDescargarRetencion && (
                      <button onClick={() => onDescargarRetencion(r)} className="text-slate-400 hover:text-primary-600 transition-colors" title="Descargar comprobante en PDF">
                        <Download size={13} />
                      </button>
                    )}
                    {onEnviarRetencion && (
                      <button onClick={() => onEnviarRetencion(r)} className="text-slate-400 hover:text-primary-600 transition-colors" title="Enviar comprobante por correo al proveedor">
                        <Mail size={13} />
                      </button>
                    )}
                  </span>
                </td>
                <td className="py-1 text-right text-slate-500">− {pm}{formatMonto(r.monto)}</td>
              </tr>
            ))}
            {!(detalle.retenciones || []).length && Number(detalle.retencion_iva_monto) > 0 && (
              <tr>
                <td className="py-1 text-slate-500" colSpan={4}>
                  Retención IVA del cliente
                  {detalle.retencion_numero_comprobante && <span className="text-slate-400"> · N° {detalle.retencion_numero_comprobante}</span>}
                </td>
                <td className="py-1 text-right text-slate-500">− {pm}{formatMonto(detalle.retencion_iva_monto)}</td>
              </tr>
            )}
            {((detalle.retenciones || []).length > 0 || Number(detalle.retencion_iva_monto) > 0) && (
              <tr className="border-t font-medium">
                <td className="py-1" colSpan={4}>Total neto {(detalle.retenciones || []).length > 0 ? 'a pagar' : 'a cobrar'}</td>
                <td className="py-1 text-right">{pm}{formatMonto(detalle.monto_adeudado)}</td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>

      {detalle.indexacion && (
        <div className="rounded-md bg-sky-50 px-3 py-2 text-xs text-sky-800">
          <p className="font-medium">Indexada a BCV</p>
          <p>
            Base Bs {formatMonto(detalle.total)} = BCV$ {formatMonto(detalle.indexacion.base_bcv)} (tasa BCV de emisión {formatMonto(detalle.indexacion.tasa_emision)}).
            El IVA y las retenciones quedan fijos en Bs.
          </p>
          {detalle.estado !== 'pagada' && detalle.estado !== 'anulada' && (
            <p className="mt-0.5">
              Se debe hoy: <strong>Bs {formatMonto(detalle.indexacion.saldo_hoy_bs)}</strong>
              {detalle.indexacion.bcv_hoy ? ` (BCV de hoy ${formatMonto(detalle.indexacion.bcv_hoy)})` : ' (falta la tasa BCV de hoy)'}
            </p>
          )}
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs font-medium text-slate-500">
            Pagos · {pm}{formatMonto(detalle.pagado)} de {pm}{formatMonto(detalle.monto_adeudado)} · saldo {pm}{formatMonto(detalle.saldo)}
            {detalle.indexacion && ' (Bs de emisión)'}
          </p>
        </div>
        {detalle.pagos.length === 0 ? (
          <p className="text-xs text-slate-400">Sin pagos registrados.</p>
        ) : (
          <table className="w-full text-xs">
            <tbody>
              {detalle.pagos.map((p) => (
                <tr key={p.id} className="border-t">
                  <td className="py-1.5 pr-2 whitespace-nowrap">{formatFecha(p.fecha)}</td>
                  <td className="py-1.5 pr-2 whitespace-nowrap text-slate-500">{p.cuenta_nombre || '—'}</td>
                  <td className="py-1.5 pr-2 text-right whitespace-nowrap">
                    {pref(p.forma)}{formatMonto(p.monto_original)}
                    {p.forma !== detalle.moneda && (
                      <span className="text-slate-400"> · {pm}{formatMonto(p.monto_factura)}</span>
                    )}
                  </td>
                  <td className="py-1.5 pr-2 text-slate-500">{p.referencia || ''}</td>
                  <td className="py-1.5 pr-2">
                    <button
                      onClick={() => onEditarPago(p)}
                      title={p.movimiento_id ? (p.movimiento_estado === 'conciliado' ? 'Conciliado con el banco — ver/editar' : 'Tiene movimiento, todavía por conciliar — ver/editar') : 'Sin conciliar — vincular a un movimiento'}
                    >
                      <BadgeVinculoPago movimientoId={p.movimiento_id} estado={p.movimiento_estado} />
                    </button>
                  </td>
                  <td className="py-1.5 text-right whitespace-nowrap">
                    <button onClick={() => onImprimirPago(p)} className="text-slate-400 hover:text-primary-600 mr-2" title="Imprimir pago">
                      <Printer size={13} />
                    </button>
                    <button onClick={() => onEditarPago(p)} className="text-slate-400 hover:text-primary-600 mr-2" title="Editar pago">
                      <Pencil size={13} />
                    </button>
                    <button onClick={() => onEliminarPago(p)} className="text-slate-400 hover:text-red-600" title="Eliminar pago">
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ---------- Pop-up para buscar y vincular un movimiento ----------
// Ordena por relevancia al pago que se gestiona (monto ≈, mismo cliente / nombre
// del proveedor, cercanía de fecha). Por defecto oculta los movimientos ya
// consumidos del todo por otros pagos; a los usados en parte les muestra el
// saldo disponible.
function PopupVincularMovimiento({
  apiBase = '/facturas', movLabel = 'ingreso',
  montoSugerido, fechaSugerida, nombreParte, clienteId, pagoActual,
  cuentaId, monedaCuenta,
  onCerrar, onElegir,
}) {
  const [q, setQ] = useState('');
  const [soloMonto, setSoloMonto] = useState(false);
  const [incluirUsados, setIncluirUsados] = useState(false);
  const [ampliar, setAmpliar] = useState(!cuentaId);
  const [resultados, setResultados] = useState([]);
  const [cargando, setCargando] = useState(false);

  const buscar = useCallback(() => {
    setCargando(true);
    api.get(`${apiBase}/movimientos-vinculables`, {
      params: {
        q: q || undefined,
        monto: montoSugerido || undefined,
        soloMonto: soloMonto ? 1 : undefined,
        incluirUsados: incluirUsados ? 1 : undefined,
        fecha: fechaSugerida || undefined,
        nombre: nombreParte || undefined,
        clienteId: clienteId || undefined,
        pagoActual: pagoActual || undefined,
        cuentaId: !ampliar && cuentaId ? cuentaId : undefined,
        monedaObjetivo: monedaCuenta || undefined,
      },
    })
      .then(({ data }) => setResultados(data))
      .finally(() => setCargando(false));
  }, [q, soloMonto, incluirUsados, montoSugerido, fechaSugerida, nombreParte, clienteId, pagoActual, apiBase, ampliar, cuentaId, monedaCuenta]);

  useEffect(() => { buscar(); }, [buscar]);

  const nombreLc = (nombreParte || '').trim().toLowerCase();
  const esSugerido = (m) => {
    const montoOk = montoSugerido && Math.abs(Number(m.monto) - Number(montoSugerido)) < 0.5;
    if (!montoOk) return false;
    if (clienteId && Number(m.cliente_id) === Number(clienteId)) return true;
    if (nombreLc) {
      const txt = `${m.descripcion || ''} ${m.referencia || ''} ${m.nombre_receptor || ''}`.toLowerCase();
      if (txt.includes(nombreLc)) return true;
    }
    return !nombreLc && !clienteId; // sin contexto de parte, monto ≈ ya alcanza
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4" onClick={onCerrar}>
      <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-800">Vincular con un movimiento</h3>
          <button onClick={onCerrar} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por descripción, referencia o receptor..."
            className="border rounded-md px-3 py-2 text-sm flex-1 min-w-[200px]"
          />
          <label className="text-xs text-slate-500 inline-flex items-center gap-1">
            <input type="checkbox" checked={soloMonto} onChange={(e) => setSoloMonto(e.target.checked)} />
            Sólo monto ≈ {formatMonto(montoSugerido)}
          </label>
          <label className="text-xs text-slate-500 inline-flex items-center gap-1">
            <input type="checkbox" checked={incluirUsados} onChange={(e) => setIncluirUsados(e.target.checked)} />
            Mostrar utilizados
          </label>
          {cuentaId && (
            <label className="text-xs text-slate-500 inline-flex items-center gap-1">
              <input type="checkbox" checked={ampliar} onChange={(e) => setAmpliar(e.target.checked)} />
              Buscar en otras cuentas
            </label>
          )}
        </div>
        {!ampliar && cuentaId && (
          <p className="text-[11px] text-slate-400 mb-2">Mostrando solo movimientos de la cuenta de este pago.</p>
        )}
        {cargando ? (
          <p className="text-slate-400 text-sm py-4 text-center">Buscando...</p>
        ) : resultados.length === 0 ? (
          <p className="text-slate-400 text-sm py-4 text-center">Ningún movimiento de {movLabel} coincide.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-slate-400 text-left text-xs">
              <tr>
                <th className="py-1">Fecha</th>
                <th className="py-1">Cuenta</th>
                <th className="py-1">Descripción</th>
                <th className="py-1">Ref.</th>
                <th className="py-1 text-right">Monto</th>
                <th className="py-1"></th>
              </tr>
            </thead>
            <tbody>
              {resultados.map((m) => {
                const usado = Number(m.ya_vinculado) === 1;
                const disponible = Number(m.disponible);
                const parcial = usado && disponible > 0.005 && disponible < Number(m.monto) - 0.005;
                return (
                  <tr key={m.id} className={`border-t ${esSugerido(m) ? 'bg-primary-50' : ''}`}>
                    <td className="py-1.5 whitespace-nowrap">{formatFecha(m.fecha)}</td>
                    <td className="py-1.5 whitespace-nowrap">{m.cuenta_nombre}</td>
                    <td className="py-1.5">
                      {esSugerido(m) && (
                        <span className="mr-1 text-[10px] font-semibold text-white bg-primary-600 rounded px-1 py-0.5 align-middle">
                          Sugerido
                        </span>
                      )}
                      {m.descripcion || '—'}
                    </td>
                    <td className="py-1.5 text-slate-500">{m.referencia || ''}</td>
                    <td className="py-1.5 text-right whitespace-nowrap">
                      {m.monto_convertido != null ? (
                        <>
                          {pref(monedaCuenta)}{formatMonto(m.monto_convertido)}
                          <span className="block text-[11px] text-slate-400">
                            ({pref(m.cuenta_moneda)}{formatMonto(m.monto)})
                          </span>
                        </>
                      ) : (
                        pref(m.cuenta_moneda) + formatMonto(m.monto)
                      )}
                      {parcial && (
                        <span className="block text-[11px] text-amber-600">
                          disp. {pref(m.cuenta_moneda)}{formatMonto(disponible)}
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 text-right">
                      <button
                        onClick={() => onElegir({
                          id: m.id, fecha: m.fecha, monto: m.monto, referencia: m.referencia,
                          descripcion: m.descripcion, cuenta_id: m.cuenta_id,
                          cuenta_nombre: m.cuenta_nombre, cuenta_moneda: m.cuenta_moneda
                        })}
                        className="text-primary-600 hover:underline text-xs inline-flex items-center gap-1"
                      >
                        <Link2 size={13} /> Vincular
                        {usado && !parcial && <span className="text-amber-500">(utilizado)</span>}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
